import { openDB, type IDBPDatabase } from 'idb';
import type { Prayer, ScriptureAnchor } from '@/types/prayer';
import type {
  AudioCaptureMode,
  AudioLocalDisposition,
  AudioRecoveryKind,
} from '@/types/audio-recovery';
import type { BibleBookmark } from '@/types/bookmark';
import type { BibleHighlight } from '@/types/highlight';
import type {
  ConflictRecordV1,
  ContentPartitionV1,
  LocalContentEntityV1,
  OrdinaryContentEnvelopeV1,
  StagedConflictV1,
  StoredPendingContentOperationV1,
} from '@/types/content-lifecycle';
import type { BsbBook, BsbInventory, BsbMeta } from '@/lib/bsb-types';
// RELATIVE import (not `@/`): decimalIncrement is a VALUE import, and the
// pre-existing db.ts probes (probe-guarded-patch-prayer, probe-highlights)
// load this module under plain tsx where the alias does not resolve. The
// keepsake/types leaf is zero-dep, so the relative path stays probe-clean.
import {
  decimalIncrement,
  type LocalKeepsakeValidityIntentV1,
  type LocalPrayerCorpusFenceV1,
  type LocalPrayerMutationFenceV1,
  type StoredPolicyPin,
} from './keepsake/types';
// RELATIVE import for the same probe-clean reason as keepsake/types above.
import {
  assertCoreWriteAllowed,
  enhancedVoiceAllowed,
  isCoreReadOnly,
} from './core-cutover';
import {
  parseScriptureAnchor,
  scriptureAnchorsEqual,
} from './scripture-anchor';

// ---- Core-content read-only write guard (db layer) ----
//
// Defense in depth for the core-content cutover: EVERY content-store write
// helper in this file calls `assertDbWrite('db:<op>')` before touching IDB,
// so a UI or store-layer bypass still rejects at the persistence boundary.
// Reads are never guarded, and the BSB / bible-progress caches stay
// writable (non-content caches; Bible reading must keep working).
//
// Test probes can seed fixtures through `__dangerouslyRunRestoreInstall`.
// Browser code cannot enter that window. The production recovery path uses
// the narrower exact-replacement transaction below.

let restoreBypassActive = false;

/** Positive runtime check so browser workers cannot impersonate Node probes. */
export function isNodeProbeRuntime(moduleUrl: string = import.meta.url): boolean {
  const runtimeProcess = (globalThis as {
    process?: { release?: { name?: string }; versions?: { node?: string } };
  }).process;
  try {
    return Boolean(
      new URL(moduleUrl).protocol === 'file:' &&
      typeof window === 'undefined' &&
      runtimeProcess?.release?.name === 'node' &&
      typeof runtimeProcess.versions?.node === 'string',
    );
  } catch {
    return false;
  }
}

/** True only inside a __dangerouslyRunRestoreInstall callback. */
export function isRestoreBypassActive(): boolean {
  return restoreBypassActive;
}

/**
 * Test-only bypass for Node probes that must seed a realistic fixture before
 * exercising the read-only guards. Browser callers are always refused. The
 * owner-bound recovery screen uses replaceCoreContentSnapshotAtomically and
 * cannot open this unrestricted write window.
 */
export async function __dangerouslyRunRestoreInstall<T>(
  fn: () => Promise<T>,
): Promise<T> {
  if (!isNodeProbeRuntime()) {
    throw new Error('restore-bypass-unavailable-in-browser');
  }
  const prev = restoreBypassActive;
  restoreBypassActive = true;
  try {
    return await fn();
  } finally {
    restoreBypassActive = prev;
  }
}

function assertDbWrite(op: string): void {
  if (restoreBypassActive) return;
  assertCoreWriteAllowed(op);
}

// CARVE-OUT (deliberate, do not "tidy" into the guard): the BSB corpus
// caches (`bsb-books`, `bsb-meta`) and the reading-position cache
// (`bible-progress`) are NOT content. They are re-derivable from the
// shipped public/bible/bsb assets, they hold nothing a user authored,
// and Bible reading has to keep working offline during the cutover, so
// their write helpers stay unguarded on purpose. Every store that can
// hold user-authored bytes (prayers, audio-queue, bible-bookmarks,
// bible-highlights, the four keepsake stores, app-meta) is guarded.

const DB_NAME = 'adia-db';
// v1: prayers store
// v2: + audio-queue store (B.2 — captured audio awaiting transcription)
// v3: + BSB local-first Bible data layer (M.3.6.3.b). Two new stores:
//     bsb-books (keyPath slug, value matches public/bible/bsb/{slug}.json
//     shape) and bsb-meta (single 'manifest' row tracking sourceSha256,
//     hydratedAt, sourceEdition for sha-based cache invalidation).
// v4: BSB data layer cutover to BsbBlock-v1 shape (M.3.8.a.2.2). Clears
//     bsb-books and bsb-meta when upgrading from v3 so bsb-bootstrap
//     re-hydrates against the new on-disk JSON shape. Defense in depth
//     alongside SHA mismatch in bsb-bootstrap. No-op for fresh installs
//     and for users upgrading from v1 or v2 (the v3 clause above creates
//     the stores fresh in the same upgrade transaction).
// v5: + bible-progress store for last-read tracking.
// v6: + bible-bookmarks store (verse-selection bookmarks + optional notes).
// v7: + app-meta store (single-row metadata). First key: the durable
//     cloud-account sentinel (offline-first Chunk 2) — written on session
//     observation, read by the boot seed to gate signed-out enrichment.
// v8: + bible-highlights store (per-verse meaning-first highlights; Bible
//     reader rethink Chunk C). keyPath 'id' (`${bookSlug}:${chapter}:${verse}`);
//     by-book-chapter (reader paint) + by-updated (journal sort) indexes.
// v9: + S0 keepsake protection stores (PC-B rev 9, sections 10 + 10b.2):
//     keepsake-fences (LocalPrayerMutationFenceV1 sidecars, one per local
//     prayer), keepsake-intents (the durable validity-intent journal),
//     keepsake-meta (the durable browser-wide local-prayer epoch + the R4
//     corpus fence), keepsake-policy-pins. The v9 upgrade transaction
//     enumerates every existing local prayer and creates each MISSING
//     fence UNRESOLVED (ownerScope null, seq 1, ack 0, no digest) BEFORE
//     any prayer UI or transport resumes; a missing sidecar is never an
//     implicit 0 == 0. NOTE: the fence instrumentation makes the prayer
//     write helpers MULTI-STORE transactions (prayers + keepsake-fences +
//     keepsake-meta) — a ratified PC-B requirement that amends the old
//     "hydrateAtomic is the only multi-store transaction" note in
//     CLAUDE.md rule #67.
// v10: + S1 ordinary-content lifecycle stores. Additive only. The legacy
//      prayer/bookmark/highlight stores are preserved byte-for-byte; the
//      owner-compounded stores become authoritative only after the later
//      Stage 3 writable release. The read-only cutover keeps opening its
//      existing version and therefore cannot activate this upgrade early.
export const DB_VERSION = 10;
// The hard-outage client must not activate the v9 keepsake layer. Existing
// databases stay at their current version while read-only, and a pristine
// recovery partition creates only the complete v8 core-content schema.
export const CORE_CUTOVER_DB_VERSION = 8;
export const PRAYERS_STORE = 'prayers';
export const AUDIO_QUEUE_STORE = 'audio-queue';
export const BSB_BOOKS_STORE = 'bsb-books';
export const BSB_META_STORE = 'bsb-meta';
export const BIBLE_PROGRESS_STORE = 'bible-progress';
export const BIBLE_BOOKMARKS_STORE = 'bible-bookmarks';
export const BIBLE_HIGHLIGHTS_STORE = 'bible-highlights';
export const APP_META_STORE = 'app-meta';
// The single app-meta row key for the durable cloud-account sentinel.
const CLOUD_ACCOUNT_META_KEY = 'cloud-account';
// S0 keepsake stores (v9).
export const KEEPSAKE_FENCES_STORE = 'keepsake-fences';
export const KEEPSAKE_INTENTS_STORE = 'keepsake-intents';
export const KEEPSAKE_META_STORE = 'keepsake-meta';
export const KEEPSAKE_PINS_STORE = 'keepsake-policy-pins';
export const CONTENT_ENTITIES_STORE = 'content-entities';
export const CONTENT_HOLDING_STORE = 'content-holding';
export const CONTENT_PENDING_STORE = 'content-pending';
export const CONTENT_PARTITIONS_STORE = 'content-partitions';
export const CONTENT_STAGED_CONFLICTS_STORE = 'content-staged-conflicts';
export const CONTENT_CONFLICTS_STORE = 'content-conflicts';
export const CONTENT_OBSERVATIONS_STORE = 'content-observations';
const KEEPSAKE_EPOCH_KEY = 'local-prayer-epoch';
const KEEPSAKE_CORPUS_FENCE_KEY = 'corpus-fence';

/**
 * Audio-queue row stored in IDB. Each entry represents a captured
 * recording awaiting transcription. The processor in `audio-queue.ts`
 * walks these on `online` / `visibilitychange → visible` and fills
 * the linked Prayer's rawText on success.
 *
 * V.A.2 storage cutover. The audio bytes for new rows live in
 * Supabase Storage at `${userId}/${id}.${ext}` in the `prayer-audio`
 * bucket — the IDB row carries `storagePath` instead of `blob`. The
 * `blob` field stays as an OPTIONAL field for two compat scenarios:
 *   - Pre-V.A.2 rows that already exist in IDB on the user's device.
 *     processOne detects missing storagePath and uploads the blob to
 *     Storage as a one-time migration (Q6).
 *   - V.A.2 client-side upload-failure rows. If `recorder.onstop`
 *     can't reach Storage (network drop, anonymous user, etc.) it
 *     enqueues with `blob` only and processOne migrates on the next
 *     online/visibilitychange-visible pass (Q5).
 * Enhanced-audio rows have at least one of `blob` or `storagePath` set.
 * Browser-recognition interruption rows may instead carry durable text with
 * no audio bytes. `processOne`'s migration branch is the only writer that
 * flips an enhanced row from blob-only to storagePath-only.
 *
 * Lifecycle (enqueue sources):
 *   - visibility-finalize (B.2 interruption): prayerId null,
 *     awaitingInlineSave falsy. Surfaces in resume card; processor
 *     auto-retries.
 *   - V.1.1 transcribe-fail-on-stop: prayerId null,
 *     awaitingInlineSave true. Excluded from resume card AND from
 *     processor auto-retry. PrayPage's Save button claims the row
 *     and calls savePendingPrayerAndLinkAudio to atomically create the
 *     pending prayer, bind prayerId, and clear the flag.
 *   - resume-card "Save what I have": Prayer is created with
 *     pendingTranscriptionId, then savePendingPrayerAndLinkAudio binds
 *     both durable records in the same transaction.
 *   - processor calls `transcribeAudio({ storagePath })`; on success,
 *     writes text to the linked Prayer (if prayerId set), dequeues
 *     the row, and deletes the Storage object.
 *   - on failure, increments attemptCount; after 6 attempts over ~2 min
 *     (or one attempt for V.B.1 terminal short-circuit codes like 413),
 *     sets the Prayer's `transcriptionFailed = true` and stops retrying.
 *     Storage audio is retained until V.B's recovery flow commits a
 *     typed version, retries, or discards (any of which deletes the
 *     Storage object via dequeueAudio).
 */
export interface AudioQueueRow {
  id: string;
  /** Owner captured at recorder entry; absent legacy rows stay quarantined. */
  ownerId?: string;
  /** Auth epoch captured at recorder entry, fencing A -> B -> A switches. */
  authEpochAtCapture?: number;
  /** Additive local envelope version. Fresh authoritative rows are v1. */
  audioProtocolVersion?: 1;
  /** Actual path used after fallback selection, never a predicted setting. */
  captureMode?: AudioCaptureMode;
  /** Stable capture destination minted before network work. */
  destinationId?: string;
  /** Browser instance that registered or recovered this row. */
  sourceDeviceId?: string;
  /**
   * Linked Prayer id, or null when the audio came from an interrupted
   * session that has not yet been committed via the resume card, or
   * from a V.1.1 transcribe-fail-on-stop that has not yet been claimed
   * by PrayPage's Save button.
   */
  prayerId: string | null;
  /**
   * V.A.2: optional. Pre-V.A.2 rows AND client-upload-fallback rows
   * carry the raw Blob in IDB; new V.A.2 rows have `storagePath`
   * instead. processOne migrates blob-only rows by uploading to
   * Storage and clearing this field. Invariant: at least one of
   * `blob` / `storagePath` must be set on a valid row.
   */
  blob?: Blob;
  /**
   * V.A.2: Supabase Storage object key in the `prayer-audio` bucket.
   * Shape: `${userId}/${id}.${ext}`. Empty/undefined means the
   * audio is still in `blob` and processOne must upload it on the
   * next pass (migration step). The leading folder MUST equal the
   * authenticated user's UUID — Storage RLS + the v2 endpoint's
   * canonical-path check both enforce this. Do not construct paths
   * elsewhere; use `uploadAudioToStorage` in audio-queue.ts.
   */
  storagePath?: string;
  /**
   * V.A.2: cached size of the audio bytes. Pre-V.A.2 rows recompute
   * via `blob.size`; new rows write this at enqueue time so the
   * audio-queue zustand mirror's deriveCounters has a size source
   * even after the blob is dropped post-migration.
   */
  sizeBytes?: number;
  /** SHA-256 of the exact Blob bytes, computed after the IDB durability seam. */
  byteSha256?: string;
  mimeType: string;
  capturedAt: string;
  language: string;
  /**
   * Filled by the processor on transcribe success. Used by the resume
   * card to pre-load PrayPage's textarea on "Continue recording" without
   * a second transcribe call.
   */
  transcribedText: string | null;
  attemptCount: number;
  /**
   * Set true after the retry budget is exhausted (or immediately by
   * V.B.1's terminal short-circuit). The processor stops retrying
   * once true. At least one durable audio location remains until the user
   * recovers through V.B's TranscriptionRecoveryBanner or explicitly
   * discards the entry.
   */
  failed: boolean;
  /**
   * V.1.1: true when this row was enqueued by useSpeechToText's
   * onstop-catch (transcribe-fail during user-initiated stop). The
   * row is excluded from both the resume-card list (via
   * useVoiceCapture's interruptedSessions filter) and the queue
   * processor's auto-retry loop. Cleared by
   * savePendingPrayerAndLinkAudio in the same transaction that creates the
   * pending Prayer and sets prayerId. Legacy rows missing this field treat
   * undefined as falsy and fall into the existing resume-card flow
   * — backward compatible without a DB_VERSION bump.
   */
  awaitingInlineSave?: boolean;
  /**
   * Source surface that produced this inline-save orphan. Absent on
   * legacy rows and all pre-bookmarks flows; normalizeSource() (in
   * audio-queue.ts) maps absent -> 'prayer'. 'bookmark' rows are NEVER
   * claimable as a prayer (positive allowlist in useVoiceCapture) and
   * cannot be claimed as a prayer. They remain recoverable until an explicit
   * discard or a durable bookmark commit. Additive on the IDB object store;
   * no DB_VERSION bump (same posture as awaitingInlineSave above).
   */
  inlineSaveSource?: 'prayer' | 'bookmark';
  /** Frozen Bible passage that an inline bookmark recording belongs to. */
  scriptureAnchor?: ScriptureAnchor;
  /** Exact destination retained when update or testimony capture fails. */
  recoveryKind?: AudioRecoveryKind;
  /** Local mirror only; server registry is authoritative for byte deletion. */
  localDisposition?: AudioLocalDisposition;
  /** Monotonic revision returned by the server registry. */
  registryRevision?: string;
  registryState?: string;
  /** Server destination lifecycle; never inferred from a local storage path. */
  destinationState?: string;
  /** Exact content receipt binding used instead of matching-text inference. */
  destinationEntityType?: 'prayer' | 'bookmark';
  destinationEntityId?: string;
  destinationSubId?: string;
  destinationOpId?: string;
  /** Recovery lease issued by the server for cross-device adoption. */
  recoveryLeaseToken?: string;
  recoveryLeaseExpiresAt?: string;
  /**
   * Capture rows written by `commitCaptureRow` only (account-change recording
   * plan rev 4.1, section 4). Set once at the first commit; the transition
   * guard refuses any later change to them, and no other writer may add them.
   * `captureEvidence` describes the captured payload, `captureIdentity` is the
   * frozen capture identity a retry is verified against, `firstPersistence`
   * records the first save (never compared, never rewritten).
   */
  captureEvidence?: CaptureEvidenceV1;
  captureIdentity?: CaptureIdentityV1;
  firstPersistence?: FirstPersistenceV1;
  /** Why the row is quarantined; set together with `localDisposition`. */
  quarantineReason?: AudioQuarantineReason;
  /** Part R: when this page's owner explicitly adopted a kept recording. */
  adoptedAt?: string;
  /** Part R: the auth epoch of that adoption (per page; never compared across tabs). */
  adoptedAuthEpoch?: number;
}

/** The captured payload: a Blob (Enhanced Voice) or sealed text (browser speech). */
export interface CaptureEvidenceV1 {
  schema: 'adia-capture-evidence-v1';
  kind: 'blob' | 'text';
  /** Lowercase hex SHA-256 of the Blob bytes, or of the UTF-8 text. */
  sha256: string;
  sizeBytes: number;
  mimeType: string;
}

/** Frozen at admission; compared when a retry verifies an existing row. */
export interface CaptureIdentityV1 {
  schema: 'adia-capture-identity-v1';
  custodyId: string;
  capturedOwnerId: string;
  capturedAuthEpoch: number;
  capturedPrayerId: string | null;
  sourceDeviceId: string;
}

export type CaptureClaimant = 'stop' | 'interruption' | 'teardown' | 'drain' | 'retry';

/** Written once by the creating commit; never compared, never rewritten. */
export interface FirstPersistenceV1 {
  schema: 'adia-first-persistence-v1';
  claimant: CaptureClaimant;
  persistedAuthUserId: string | null;
  persistedAuthEpoch: number;
  networkBeforePersist: false;
}

export type AudioQuarantineReason =
  | 'account-change-preservation'
  | 'registry-conflict'
  | 'registry-revision-malformed'
  | 'registry-state-conflict';

/**
 * True when a queue row still carries, or indexes, recoverable audio bytes.
 * IndexedDB is an untrusted historical boundary, so any present value (even a
 * malformed one such as `null`) stays fail-closed rather than being treated as
 * an absent Blob/path.
 */
export function audioQueueRowCarriesBytes(
  row: Pick<AudioQueueRow, 'blob' | 'storagePath'>,
): boolean {
  return row.blob !== undefined || row.storagePath !== undefined;
}

/** A durable transcript with no remaining byte location is safe local text. */
export function audioQueueRowIsTextOnly(
  row: Pick<AudioQueueRow, 'blob' | 'storagePath' | 'transcribedText'>,
): boolean {
  return (
    !audioQueueRowCarriesBytes(row) &&
    typeof row.transcribedText === 'string' &&
    row.transcribedText.trim() !== ''
  );
}

export interface BibleProgressLastRead {
  /** Owner-compounded `${ownerId}:last-read`; legacy global rows stay inert. */
  key: string;
  ownerId: string;
  bookSlug: string;
  chapter: number;
  scrollOffset: number;
  timestamp: number;
}

export interface BibleRecentRead {
  bookSlug: string;
  chapter: number;
  scrollOffset: number;
  timestamp: number;
  /**
   * Nearest visible verse at the time of the last checkpoint (reading-history
   * arc). Additive optional — legacy rows lack it and every consumer treats
   * absence as "no verse context". No DB_VERSION bump (value-shape only,
   * same posture as inlineSaveSource above).
   */
  verse?: number;
}

export interface BibleProgressRecentReads {
  /** Owner-compounded `${ownerId}:recent-reads`; legacy global rows stay inert. */
  key: string;
  ownerId: string;
  entries: BibleRecentRead[];
}

interface AdiaDB {
  prayers: {
    key: string;
    value: Prayer;
    indexes: { 'by-created': string; 'by-status': string };
  };
  'audio-queue': {
    key: string;
    value: AudioQueueRow;
    indexes: { 'by-captured': string };
  };
  'bsb-books': {
    key: string;
    value: BsbBook;
  };
  // Two rows since 2026-08-25: 'manifest' (BsbMeta, the complete-corpus
  // signal) and 'inventory' (BsbInventory, the resumable first-hydration
  // checkpoint). Same store, additive value union — no DB_VERSION bump.
  'bsb-meta': {
    key: 'manifest' | 'inventory';
    value: BsbMeta | BsbInventory;
  };
  'bible-progress': {
    key: string;
    value: BibleProgressLastRead | BibleProgressRecentReads;
  };
  'bible-bookmarks': {
    key: string;
    value: BibleBookmark;
    indexes: { 'by-book-chapter': [string, number]; 'by-updated': string };
  };
  'bible-highlights': {
    key: string;
    value: BibleHighlight;
    indexes: { 'by-book-chapter': [string, number]; 'by-updated': string };
  };
  // Two row families since the core-content cutover: the durable
  // cloud-account sentinel ({ value: boolean }, v7) and the durable
  // content-owner binding ({ value: string }, src/lib/content-owner.ts).
  // Additive value union, no DB_VERSION bump (same posture as the
  // 'bsb-meta' manifest/inventory union above).
  'app-meta': {
    key: string;
    value: { key: string; value: boolean | string };
  };
  'keepsake-fences': {
    key: string;
    value: LocalPrayerMutationFenceV1;
  };
  'keepsake-intents': {
    key: string;
    value: LocalKeepsakeValidityIntentV1;
  };
  'keepsake-meta': {
    key: string;
    value:
      | { key: 'local-prayer-epoch'; value: string }
      | { key: 'corpus-fence'; fence: LocalPrayerCorpusFenceV1 };
  };
  'keepsake-policy-pins': {
    key: string;
    value: StoredPolicyPin;
  };
  'content-entities': {
    key: [string, string, string];
    value: LocalContentEntityV1;
    indexes: {
      'by-owner': string;
      'by-owner-state': [string, string];
    };
  };
  'content-holding': {
    key: [string, number];
    value: OrdinaryContentEnvelopeV1;
    indexes: { 'by-owner': string };
  };
  'content-pending': {
    key: [string, string];
    value: StoredPendingContentOperationV1;
    indexes: {
      'by-owner': string;
      'by-owner-entity': [string, string, string];
    };
  };
  'content-partitions': {
    key: string;
    value: ContentPartitionV1;
  };
  'content-staged-conflicts': {
    key: [string, string, string];
    value: StagedConflictV1;
    indexes: { 'by-owner': string };
  };
  'content-conflicts': {
    key: [string, string, string];
    value: ConflictRecordV1;
    indexes: { 'by-owner': string };
  };
  'content-observations': {
    key: [string, string];
    value: {
      ownerId: string;
      observationKey: string;
      reason:
        | 'unknown-content-protocol'
        | 'digest-mismatch'
        | 'owner-scope-mismatch'
        | 'malformed-envelope'
        | 'unverifiable-owner';
      raw: unknown;
      observedAt: string;
    };
    indexes: { 'by-owner': string };
  };
}

let dbPromise: Promise<IDBPDatabase<AdiaDB>> | null = null;
let persistRequested = false;
let writableUpgradeProbeActive = false;

/** Node-only seam for exercising the real historical upgrade ladder. */
export function __dangerouslyEnableWritableUpgradeProbe(): void {
  if (!isNodeProbeRuntime()) {
    throw new Error('writable-upgrade-probe-unavailable-in-browser');
  }
  writableUpgradeProbeActive = true;
}

export const CLIENT_UPDATE_REQUIRED_EVENT = 'adia:client-update-required';

export class ClientUpdateRequiredError extends Error {
  constructor() {
    super('client-update-required');
    this.name = 'ClientUpdateRequiredError';
  }
}

export function isClientUpdateRequiredError(error: unknown): boolean {
  return (
    error instanceof ClientUpdateRequiredError ||
    (error instanceof DOMException && error.name === 'VersionError') ||
    (error instanceof Error && (
      error.name === 'VersionError' ||
      error.message === 'client-update-required' ||
      error.message === 'core-read-only-db-version-unsupported'
    ))
  );
}

function signalClientUpdateRequired(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(CLIENT_UPDATE_REQUIRED_EVENT));
  }
}

async function databaseVersionForOpen(): Promise<number> {
  if (writableUpgradeProbeActive || !isCoreReadOnly()) return DB_VERSION;
  if (typeof indexedDB.databases !== 'function') {
    throw new Error('core-read-only-db-inventory-unavailable');
  }
  const inventory = await indexedDB.databases();
  const existing = inventory.find((entry) => entry.name === DB_NAME);
  if (!existing) return CORE_CUTOVER_DB_VERSION;
  const version = existing.version;
  if (version === undefined || !Number.isSafeInteger(version) || version < 1) {
    throw new Error('core-read-only-db-version-invalid');
  }
  if (version > DB_VERSION) {
    throw new ClientUpdateRequiredError();
  }
  return version;
}

function requestPersistence() {
  if (persistRequested) return;
  persistRequested = true;
  if (navigator.storage?.persist) {
    navigator.storage.persist().then((granted) => {
      if (granted) console.log('[Adia] Persistent storage granted');
    }).catch(() => {});
  }
}

export function getDb() {
  if (!dbPromise) {
    requestPersistence();
    const opening = databaseVersionForOpen().then((openVersion) =>
      openDB<AdiaDB>(DB_NAME, openVersion, {
        upgrade(db, oldVersion, newVersion, upgradeTx) {
          if (newVersion === null) throw new Error('adia-db-upgrade-version-missing');
        // v1 → prayers store. Idempotent guard via objectStoreNames so
        // repeated upgrades from sub-versions don't double-create.
        if (oldVersion < 1 && newVersion >= 1) {
          const store = db.createObjectStore(PRAYERS_STORE, { keyPath: 'id' });
          store.createIndex('by-created', 'createdAt');
          store.createIndex('by-status', 'status');
        }
        // v2 → audio-queue store. Additive only; existing prayers store
        // is untouched. Metadata-only operation, sub-millisecond on
        // first-load post-deploy. No data migration.
        if (oldVersion < 2 && newVersion >= 2) {
          const queue = db.createObjectStore(AUDIO_QUEUE_STORE, { keyPath: 'id' });
          queue.createIndex('by-captured', 'capturedAt');
        }
        // v3 → BSB Bible data layer. Two additive stores. No indexes;
        // lookups are by primary key (slug for books, the constant
        // 'manifest' for meta). Metadata-only operation, sub-millisecond
        // on first-load post-deploy. No data migration.
        if (oldVersion < 3 && newVersion >= 3) {
          db.createObjectStore(BSB_BOOKS_STORE, { keyPath: 'slug' });
          db.createObjectStore(BSB_META_STORE, { keyPath: 'key' });
        }
        // v4 -> BSB data layer cutover to BsbBlock-v1 shape. Clear the
        // v3 bsb-books and bsb-meta stores so bsb-bootstrap re-hydrates
        // against the new on-disk JSON shape. SHA mismatch in
        // bsb-bootstrap also triggers re-hydration; this clause is
        // defense in depth so old flat-shape cached data cannot leak
        // to new block-shape callers.
        //
        // Guard: only run when upgrading FROM v3. Users coming from v1
        // or v2 had the v3 clause above just create fresh empty stores
        // in the same upgrade transaction, so the v4 clause is a no-op
        // for them.
        if (oldVersion >= 3 && oldVersion < 4 && newVersion >= 4) {
          if (db.objectStoreNames.contains(BSB_BOOKS_STORE)) {
            db.deleteObjectStore(BSB_BOOKS_STORE);
          }
          if (db.objectStoreNames.contains(BSB_META_STORE)) {
            db.deleteObjectStore(BSB_META_STORE);
          }
          db.createObjectStore(BSB_BOOKS_STORE, { keyPath: 'slug' });
          db.createObjectStore(BSB_META_STORE, { keyPath: 'key' });
        }
        // v5 -> Bible progress: last-read tracking for the Bible reader.
        // Single-row store keyed by the constant 'last-read'. (Bookmark
        // storage was once expected to ride this same bump, but a store
        // cannot be added retroactively to v5 once it has shipped; it
        // landed as the additive v6 store below.)
        if (oldVersion < 5 && newVersion >= 5) {
          db.createObjectStore(BIBLE_PROGRESS_STORE, { keyPath: 'key' });
        }
        // v6 -> bible-bookmarks: verse-selection bookmarks with optional
        // notes (Bible reader). keyPath 'id'. The by-book-chapter compound
        // index serves fast per-chapter reads (reader markers); by-updated
        // serves the Recent-list sort and the outbox stale-scan. Additive,
        // metadata-only, sub-millisecond on first-load post-deploy.
        if (oldVersion < 6 && newVersion >= 6) {
          const bookmarks = db.createObjectStore(BIBLE_BOOKMARKS_STORE, {
            keyPath: 'id',
          });
          bookmarks.createIndex('by-book-chapter', ['bookSlug', 'chapter']);
          bookmarks.createIndex('by-updated', 'updatedAt');
        }
        // v7 -> app-meta: single-row metadata store. First/only key today is
        // the durable cloud-account sentinel (offline-first Chunk 2): written
        // on session observation (sign-in / successful push), read by the boot
        // seed to gate signed-out enrichment. keyPath 'key'. Co-located with
        // the prayer rows it gates so a wiped IDB takes both with it (nothing
        // to clobber) and a surviving IDB carries the sentinel alongside them.
        // Additive, metadata-only, sub-millisecond on first-load post-deploy.
        if (oldVersion < 7 && newVersion >= 7) {
          db.createObjectStore(APP_META_STORE, { keyPath: 'key' });
        }
        // v8 -> bible-highlights: per-verse meaning-first highlights (Bible
        // reader rethink Chunk C). keyPath 'id'
        // (`${bookSlug}:${chapter}:${verse}`). by-book-chapter serves fast
        // per-chapter reads (reader paint); by-updated serves the journal sort
        // + the outbox stale-scan. Additive, metadata-only, sub-millisecond on
        // first-load post-deploy. Unconditional (runs for every user); the
        // store is only WRITTEN under the v2 flag, harmless otherwise.
        if (oldVersion < 8 && newVersion >= 8) {
          const highlights = db.createObjectStore(BIBLE_HIGHLIGHTS_STORE, {
            keyPath: 'id',
          });
          highlights.createIndex('by-book-chapter', ['bookSlug', 'chapter']);
          highlights.createIndex('by-updated', 'updatedAt');
        }
        // v9 -> S0 keepsake protection stores (PC-B rev 9). Four additive
        // stores, PLUS the CR-upgrade fence enumeration: inside this
        // versionchange transaction (which blocks every other transaction,
        // i.e. "before any prayer UI or transport resumes"), every
        // existing local prayer gets a fence sidecar created UNRESOLVED —
        // ownerScope null, localMutationSeq '1', acknowledgedMutationSeq
        // '0', no in-flight sequence, payloadDigest null — because no
        // owner is yet safe to place in KeepsakePrayerSourceV1. The
        // fences stay blocked until R4 reconciliation or an explicit
        // later user resolution; an upgrade failure aborts the whole
        // versionchange (boot failure keeps the local gate closed).
        if (oldVersion < 9 && newVersion >= 9) {
          db.createObjectStore(KEEPSAKE_FENCES_STORE, { keyPath: 'prayerId' });
          db.createObjectStore(KEEPSAKE_INTENTS_STORE, { keyPath: 'mutationId' });
          db.createObjectStore(KEEPSAKE_META_STORE, { keyPath: 'key' });
          db.createObjectStore(KEEPSAKE_PINS_STORE, { keyPath: 'key' });
          const prayersStore = upgradeTx.objectStore(PRAYERS_STORE);
          const fencesStore = upgradeTx.objectStore(KEEPSAKE_FENCES_STORE);
          void prayersStore.getAllKeys().then((keys) => {
            for (const key of keys) {
              void fencesStore.put({
                prayerId: String(key),
                ownerScope: null,
                localMutationSeq: '1',
                acknowledgedMutationSeq: '0',
                inFlightMutationSeq: null,
                payloadDigest: null,
                authEpochAtMutation: 0,
              });
            }
          });
          void upgradeTx.objectStore(KEEPSAKE_META_STORE).put({
            key: 'local-prayer-epoch',
            value: '1',
          });
        }
        // v10 -> S1 ordinary-content lifecycle. Every new primary key starts
        // with ownerId. No existing store is cleared, renamed, or rewritten.
        // Legacy bytes remain recoverable while Stage 3 is blocked.
        if (oldVersion < 10 && newVersion >= 10) {
          const entities = db.createObjectStore(CONTENT_ENTITIES_STORE, {
            keyPath: ['ownerId', 'entityType', 'entityId'],
          });
          entities.createIndex('by-owner', 'ownerId');
          entities.createIndex('by-owner-state', ['ownerId', 'state']);

          const holding = db.createObjectStore(CONTENT_HOLDING_STORE, {
            keyPath: ['ownerId', 'contentRevision'],
          });
          holding.createIndex('by-owner', 'ownerId');
          const pending = db.createObjectStore(CONTENT_PENDING_STORE, {
            keyPath: ['ownerId', 'opId'],
          });
          pending.createIndex('by-owner', 'ownerId');
          pending.createIndex('by-owner-entity', ['ownerId', 'entityType', 'entityId']);
          db.createObjectStore(CONTENT_PARTITIONS_STORE, { keyPath: 'ownerId' });

          const staged = db.createObjectStore(CONTENT_STAGED_CONFLICTS_STORE, {
            keyPath: ['ownerId', 'entityType', 'entityId'],
          });
          staged.createIndex('by-owner', 'ownerId');

          const conflicts = db.createObjectStore(CONTENT_CONFLICTS_STORE, {
            keyPath: ['ownerId', 'entityType', 'entityId'],
          });
          conflicts.createIndex('by-owner', 'ownerId');

          const observations = db.createObjectStore(CONTENT_OBSERVATIONS_STORE, {
            keyPath: ['ownerId', 'observationKey'],
          });
          observations.createIndex('by-owner', 'ownerId');
        }
        },
        blocked() {
          // Another tab still holds an older connection. Painting an endless
          // loading screen would hide the only useful recovery action.
          signalClientUpdateRequired();
        },
        blocking(_currentVersion, _blockedVersion, event) {
          // Cooperate with a newer tab by releasing this document's cached
          // connection, then require a reload so its bundled schema and the
          // on-disk schema cannot diverge.
          const rawDb = event.target;
          if (rawDb instanceof IDBDatabase) rawDb.close();
          dbPromise = null;
          signalClientUpdateRequired();
        },
        terminated() {
          dbPromise = null;
          signalClientUpdateRequired();
        },
      }),
    );
    dbPromise = opening.catch((error: unknown) => {
      if (isClientUpdateRequiredError(error)) {
        signalClientUpdateRequired();
        throw new ClientUpdateRequiredError();
      }
      throw error;
    });
    // A failed open must not poison the rest of the page session: clear
    // the cached promise so the next getDb() call retries fresh
    // (2026-08-25). Previously a transient open failure (Safari does
    // this under storage pressure / cold PWA starts) latched the
    // rejected promise, so every subsequent IDB read/write — and every
    // user-tapped Retry — kept failing until a full reload. Callers
    // still see this rejection; only the cache resets.
    dbPromise.catch(() => {
      dbPromise = null;
    });
  }
  return dbPromise;
}

export async function getAllPrayers(): Promise<Prayer[]> {
  const db = await getDb();
  const all = await db.getAll(PRAYERS_STORE);
  return all.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

export async function getPrayer(id: string): Promise<Prayer | undefined> {
  const db = await getDb();
  return db.get(PRAYERS_STORE, id);
}

// ---- S0 fence instrumentation (PC-B rev 9 section 10) ----
//
// Every render-relevant local prayer mutation increments its
// LocalPrayerMutationFenceV1 sidecar TRANSACTIONALLY (same IDB
// transaction as the prayer write) and advances the durable browser-wide
// local prayer epoch, BEFORE the changed row is exposed. Pending is
// never inferred from timestamps. `payloadDigest` is patched
// asynchronously afterwards by fence code (keepsake/fences.ts) because
// WebCrypto hashing cannot run inside an IDB transaction; an unpatched
// digest simply cannot acknowledge (fail closed).
//
// `mode` semantics:
//   'local'  — a locally-authored mutation: localMutationSeq advances
//              alone (unacknowledged until a matching push response).
//   'pulled' — a sync-pull application: local and acknowledged sequences
//              advance TOGETHER (the row IS server state).
type FenceWriteMode = 'local' | 'pulled';

/**
 * Render-relevant = any KeepsakePrayerSourceV1 field (the same set the
 * 034 server trigger compares). Bookkeeping-only writes (syncedAt,
 * pendingEnrichment, pendingTranscriptionId, verseResult, anchors) do
 * NOT advance the fence — otherwise the post-push syncedAt stamp would
 * re-close the keepsake gate after every successful acknowledgement.
 */
function renderRelevantChanged(oldRow: Prayer | undefined, newRow: Prayer): boolean {
  if (!oldRow) return true;
  if (oldRow.rawText !== newRow.rawText) return true;
  if (JSON.stringify(oldRow.parsed ?? null) !== JSON.stringify(newRow.parsed ?? null)) return true;
  if ((oldRow.status ?? 'Open') !== (newRow.status ?? 'Open')) return true;
  if (
    JSON.stringify(oldRow.progressHistory ?? []) !== JSON.stringify(newRow.progressHistory ?? [])
  ) {
    return true;
  }
  if ((oldRow.gardenSummary ?? null) !== (newRow.gardenSummary ?? null)) return true;
  if ((oldRow.testimony ?? null) !== (newRow.testimony ?? null)) return true;
  if ((oldRow.isPrivate ?? false) !== (newRow.isPrivate ?? false)) return true;
  if (oldRow.createdAt !== newRow.createdAt) return true;
  if (oldRow.updatedAt !== newRow.updatedAt) return true;
  return false;
}

function bumpFenceIn(
  fence: LocalPrayerMutationFenceV1 | undefined,
  prayerId: string,
  mode: FenceWriteMode,
  authEpoch: number,
): LocalPrayerMutationFenceV1 {
  const base: LocalPrayerMutationFenceV1 = fence ?? {
    prayerId,
    ownerScope: null,
    localMutationSeq: '0',
    acknowledgedMutationSeq: '0',
    inFlightMutationSeq: null,
    payloadDigest: null,
    authEpochAtMutation: authEpoch,
  };
  const nextLocal = decimalIncrement(base.localMutationSeq);
  return {
    ...base,
    localMutationSeq: nextLocal,
    acknowledgedMutationSeq: mode === 'pulled' ? nextLocal : base.acknowledgedMutationSeq,
    inFlightMutationSeq: null,
    payloadDigest: null,
    authEpochAtMutation: authEpoch,
  };
}

// Injected by keepsake/epochs.ts at boot so db.ts (a leaf many modules
// import) does not import the epoch module and create a cycle.
let currentAuthEpochFn: () => number = () => 0;
export function _setAuthEpochSource(fn: () => number): void {
  currentAuthEpochFn = fn;
}
// Post-write fence digest finalizer, injected by keepsake/fences.ts.
let fenceDigestFinalizer: ((prayerId: string, seq: string) => void) | null = null;
export function _setFenceDigestFinalizer(fn: (prayerId: string, seq: string) => void): void {
  fenceDigestFinalizer = fn;
}
// Epoch broadcast hook, injected by keepsake/epochs.ts.
let epochBroadcast: ((value: string) => void) | null = null;
export function _setEpochBroadcast(fn: (value: string) => void): void {
  epochBroadcast = fn;
}

type KeepsakeWriteTx = {
  objectStore(name: 'keepsake-fences'): {
    get(id: string): Promise<LocalPrayerMutationFenceV1 | undefined>;
    put(v: LocalPrayerMutationFenceV1): Promise<unknown> | unknown;
    delete(id: string): Promise<unknown> | unknown;
  };
  objectStore(name: 'keepsake-meta'): {
    get(id: string): Promise<{ key: string; value?: string } | undefined>;
    put(v: { key: 'local-prayer-epoch'; value: string }): Promise<unknown> | unknown;
  };
};

/**
 * Observe a queued request whose result the caller does not await. When the
 * transaction aborts (a refused put, a storage failure, an injected fault), the
 * request promise rejects; without a handler that rejection surfaces as an
 * unhandled page error even though the abort is reported once, by the awaited
 * `tx.done` or the thrown refusal. The handler changes no outcome: the awaited
 * `tx.done` still rejects for the caller.
 */
function observeQueuedWrite(request: unknown): void {
  // The keepsake transaction type returns `Promise<unknown> | unknown` from
  // put; Promise.resolve returns a native promise unchanged, so the handler
  // attaches to the real request promise when there is one.
  void Promise.resolve(request).catch(() => {});
}

async function bumpFenceAndEpochInTx(
  tx: KeepsakeWriteTx,
  prayerId: string,
  mode: FenceWriteMode,
): Promise<{ seq: string; epoch: string }> {
  const fences = tx.objectStore('keepsake-fences');
  const meta = tx.objectStore('keepsake-meta');
  const fence = await fences.get(prayerId);
  const next = bumpFenceIn(fence, prayerId, mode, currentAuthEpochFn());
  observeQueuedWrite(fences.put(next));
  const epochRow = await meta.get(KEEPSAKE_EPOCH_KEY);
  const nextEpoch = decimalIncrement(
    epochRow && typeof epochRow.value === 'string' ? epochRow.value : '0',
  );
  observeQueuedWrite(meta.put({ key: 'local-prayer-epoch', value: nextEpoch }));
  return { seq: next.localMutationSeq, epoch: nextEpoch };
}

function afterFenceWrite(prayerId: string, seq: string, epoch: string): void {
  epochBroadcast?.(epoch);
  fenceDigestFinalizer?.(prayerId, seq);
}

/**
 * S1 bridge for the owner-compounded lifecycle transaction. The caller must
 * include both keepsake stores in the SAME readwrite transaction as the
 * lifecycle entity write. New S1 rows are owner-verifiable, so their fence is
 * scoped immediately instead of inheriting the pre-R4 null-owner posture.
 */
export async function bumpLifecyclePrayerFenceInTx(
  tx: KeepsakeWriteTx,
  prayerId: string,
  ownerId: string,
  mode: FenceWriteMode,
): Promise<{ seq: string; epoch: string }> {
  const marks = await bumpFenceAndEpochInTx(tx, prayerId, mode);
  const fences = tx.objectStore(KEEPSAKE_FENCES_STORE);
  const current = await fences.get(prayerId);
  if (!current) throw new Error('content-lifecycle-fence-missing');
  void fences.put({ ...current, ownerScope: ownerId });
  return marks;
}

export async function acknowledgeLifecyclePrayerFenceInTx(
  tx: KeepsakeWriteTx,
  prayerId: string,
  ownerId: string,
  acknowledgedMutationSeq: string,
): Promise<boolean> {
  const fences = tx.objectStore(KEEPSAKE_FENCES_STORE);
  const current = await fences.get(prayerId);
  if (!current || current.ownerScope !== ownerId) {
    throw new Error('content-lifecycle-fence-owner-mismatch');
  }
  if (current.localMutationSeq !== acknowledgedMutationSeq) return false;
  void fences.put({
    ...current,
    acknowledgedMutationSeq,
    inFlightMutationSeq: null,
  });
  return true;
}

/** Run only after the encompassing lifecycle transaction commits. */
export function finalizeLifecyclePrayerFence(
  prayerId: string,
  marks: { seq: string; epoch: string },
): void {
  afterFenceWrite(prayerId, marks.seq, marks.epoch);
}

export async function savePrayer(prayer: Prayer): Promise<void> {
  assertDbWrite('db:prayer:put');
  const db = await getDb();
  const tx = db.transaction(
    [PRAYERS_STORE, KEEPSAKE_FENCES_STORE, KEEPSAKE_META_STORE],
    'readwrite',
  );
  const prayers = tx.objectStore(PRAYERS_STORE);
  const existing = await prayers.get(prayer.id);
  void prayers.put(prayer);
  let marks: { seq: string; epoch: string } | null = null;
  if (renderRelevantChanged(existing, prayer)) {
    marks = await bumpFenceAndEpochInTx(tx as unknown as KeepsakeWriteTx, prayer.id, 'local');
  }
  await tx.done;
  if (marks) afterFenceWrite(prayer.id, marks.seq, marks.epoch);
}

export interface FailedPendingPrayerTranscription {
  prayer: Prayer;
  row: AudioQueueRow;
}

/**
 * Atomically surface a terminal audio failure on both durable halves. The
 * owner binding, pending queue id, prayer, and queue row are compared in one
 * transaction so a late tab cannot recreate or strand a discarded recovery.
 */
export async function failPendingPrayerTranscription(
  prayerId: string,
  queueItemId: string,
  expectedOwnerId: string,
  terminalAttemptCount: number,
  verifiedCopy?: VerifiedCaptureCopy | null,
): Promise<FailedPendingPrayerTranscription | null> {
  assertDbWrite('db:prayer-audio:terminal-failure');
  if (!Number.isSafeInteger(terminalAttemptCount) || terminalAttemptCount < 1) {
    throw new Error('invalid-terminal-attempt-count');
  }
  // KR8: the terminal write keeps the recording; it stores a verified copy.
  const copy = await verifiedCaptureCopyBeforeWrite(queueItemId, expectedOwnerId, verifiedCopy);
  const db = await getDb();
  const tx = db.transaction(
    [
      AUDIO_QUEUE_STORE,
      PRAYERS_STORE,
      APP_META_STORE,
      KEEPSAKE_FENCES_STORE,
      KEEPSAKE_META_STORE,
    ],
    'readwrite',
  );
  const binding = await tx.objectStore(APP_META_STORE).get(CONTENT_OWNER_META_KEY);
  const queue = tx.objectStore(AUDIO_QUEUE_STORE);
  const prayers = tx.objectStore(PRAYERS_STORE);
  const row = await queue.get(queueItemId);
  const current = await prayers.get(prayerId);
  if (
    binding?.value !== expectedOwnerId ||
    !row ||
    row.prayerId !== prayerId ||
    (row.recoveryKind != null && row.recoveryKind !== 'new-prayer') ||
    !current ||
    current.pendingTranscriptionId !== queueItemId
  ) {
    await tx.done;
    return null;
  }
  const next: Prayer = {
    ...current,
    transcriptionFailed: true,
  };
  const nextRow = keepVerifiedCaptureCopy(row, {
    ...row,
    attemptCount: terminalAttemptCount,
    failed: true,
  }, copy);
  const captureRefusal = captureRowTransitionRefusal(row, nextRow);
  if (captureRefusal) return abortForCaptureRefusal(tx, captureRefusal);
  // An abort after this point is reported once, by `tx.done` or the thrown
  // refusal; the queued requests and the transaction promise are observed so
  // that it never also surfaces as an unhandled rejection.
  void tx.done.catch(() => {});
  observeQueuedWrite(prayers.put(next));
  observeQueuedWrite(queue.put(nextRow));
  const marks = await bumpFenceAndEpochInTx(
    tx as unknown as KeepsakeWriteTx,
    prayerId,
    'local',
  );
  await tx.done;
  afterFenceWrite(prayerId, marks.seq, marks.epoch);
  return { prayer: next, row: nextRow };
}

const AUDIO_OWNER_UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const AUDIO_QUEUE_UUID_V4 =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
// Mirrors the client upload allowlist in audio-queue.ts. It stays local here
// because db.ts is the persistence leaf and importing the queue orchestrator
// would create a cycle. The server independently mirrors the same canonical
// migration-backed set in api/_lib/storage-constants.ts.
const PENDING_AUDIO_EXTENSIONS = new Set(['webm', 'm4a', 'mp3', 'wav', 'ogg']);

type PendingPrayerAudioDisposition =
  | { state: 'processable' | 'terminal' }
  | { state: 'refused'; reason: string };

function classifyPendingPrayerAudio(
  row: AudioQueueRow,
  queueItemId: string,
  expectedOwnerId: string,
): PendingPrayerAudioDisposition {
  if (
    row.id !== queueItemId ||
    !AUDIO_OWNER_UUID.test(expectedOwnerId) ||
    !AUDIO_QUEUE_UUID_V4.test(queueItemId) ||
    typeof row.mimeType !== 'string' ||
    row.mimeType.trim() === '' ||
    typeof row.capturedAt !== 'string' ||
    !Number.isFinite(Date.parse(row.capturedAt)) ||
    typeof row.language !== 'string' ||
    row.language.trim() === '' ||
    !(row.transcribedText === null || typeof row.transcribedText === 'string') ||
    !Number.isSafeInteger(row.attemptCount) ||
    row.attemptCount < 0 ||
    typeof row.failed !== 'boolean' ||
    (row.awaitingInlineSave !== undefined && typeof row.awaitingInlineSave !== 'boolean')
  ) {
    return { state: 'refused', reason: 'pending-prayer-audio-invalid' };
  }

  if (
    (row.inlineSaveSource != null && row.inlineSaveSource !== 'prayer') ||
    (row.recoveryKind != null && row.recoveryKind !== 'new-prayer')
  ) {
    return { state: 'refused', reason: 'pending-prayer-audio-destination-mismatch' };
  }

  const blob = row.blob;
  if (blob !== undefined && !(blob instanceof Blob)) {
    return { state: 'refused', reason: 'pending-prayer-audio-invalid' };
  }
  const hasBlob = blob instanceof Blob;
  if (
    hasBlob &&
    (blob.size <= 0 || (blob.type !== '' && blob.type !== row.mimeType))
  ) {
    return { state: 'refused', reason: 'pending-prayer-audio-invalid' };
  }

  if (row.storagePath !== undefined && typeof row.storagePath !== 'string') {
    return { state: 'refused', reason: 'pending-prayer-audio-invalid' };
  }
  const hasStorage = typeof row.storagePath === 'string' && row.storagePath.length > 0;
  if (hasStorage) {
    const prefix = `${expectedOwnerId}/${queueItemId}.`;
    const extension = row.storagePath!.startsWith(prefix)
      ? row.storagePath!.slice(prefix.length)
      : '';
    if (
      extension === '' ||
      extension.includes('/') ||
      !PENDING_AUDIO_EXTENSIONS.has(extension.toLowerCase())
    ) {
      return { state: 'refused', reason: 'pending-prayer-audio-owner-mismatch' };
    }
  }

  if (row.sizeBytes !== undefined) {
    if (
      !Number.isSafeInteger(row.sizeBytes) ||
      row.sizeBytes < 0 ||
      ((hasBlob || hasStorage) && row.sizeBytes === 0) ||
      (hasBlob && row.sizeBytes !== blob.size)
    ) {
      return { state: 'refused', reason: 'pending-prayer-audio-invalid' };
    }
  }

  const hasTranscript =
    typeof row.transcribedText === 'string' && row.transcribedText.trim() !== '';
  const hasAudio = hasBlob || hasStorage;
  if ((!hasTranscript && !hasAudio) || (hasAudio && !row.mimeType.startsWith('audio/'))) {
    return { state: 'refused', reason: 'pending-prayer-audio-unprocessable' };
  }
  // A failed text-only row is internally contradictory: the processor skips
  // failed rows, while there are no retained bytes for the recovery surface.
  if (row.failed && !hasAudio) {
    return { state: 'refused', reason: 'pending-prayer-audio-unprocessable' };
  }

  return { state: row.failed ? 'terminal' : 'processable' };
}

export interface PendingPrayerAudioLink {
  prayer: Prayer;
  row: AudioQueueRow;
}

/**
 * Create a local pending Prayer and bind its recovery audio in one IDB
 * transaction. The owner binding, fresh queue state, prayer row, render fence,
 * and local epoch either all commit or all roll back. A terminal queue row is
 * linked only with a matching `transcriptionFailed` projection, so an empty
 * Prayer always has a truthful recovery surface rather than an invisible,
 * permanently skipped queue entry. `verifiedCopy` (KR8, "Verified capture
 * copies"): the caller's proved copy of the row's bytes, stored in place of the
 * Blob read here when the guard accepts it for that row; otherwise the link is
 * exactly as without it.
 */
export async function savePendingPrayerAndLinkAudio(
  prayer: Prayer,
  queueItemId: string,
  expectedOwnerId: string,
  verifiedCopy?: VerifiedCaptureCopy | null,
): Promise<PendingPrayerAudioLink> {
  assertDbWrite('db:prayer-audio:create-pending');
  if (prayer.pendingTranscriptionId !== queueItemId) {
    throw new Error('pending-prayer-audio-id-mismatch');
  }
  const copy = await verifiedCaptureCopyBeforeWrite(queueItemId, expectedOwnerId, verifiedCopy);

  const db = await getDb();
  const tx = db.transaction(
    [
      PRAYERS_STORE,
      AUDIO_QUEUE_STORE,
      APP_META_STORE,
      KEEPSAKE_FENCES_STORE,
      KEEPSAKE_META_STORE,
    ],
    'readwrite',
  );
  const abort = async (message: string): Promise<never> => {
    tx.abort();
    await tx.done.catch(() => {});
    throw new Error(message);
  };

  const binding = await tx.objectStore(APP_META_STORE).get(CONTENT_OWNER_META_KEY);
  if (binding?.value !== expectedOwnerId) {
    return abort('pending-prayer-owner-changed');
  }

  const prayers = tx.objectStore(PRAYERS_STORE);
  if (await prayers.get(prayer.id)) {
    return abort('pending-prayer-id-exists');
  }

  const audio = tx.objectStore(AUDIO_QUEUE_STORE);
  const row = await audio.get(queueItemId);
  if (!row) return abort('pending-prayer-audio-missing');
  if (row.prayerId !== null) {
    return abort('pending-prayer-audio-already-linked');
  }
  // A recording whose explicit discard is already recorded (audio-queue.ts
  // dequeueAudio writes `discard-pending` before any server effect) is no
  // longer this Save's to link. As with the content commit's claim, which
  // requires `unresolved`, the link and the discard exclude each other here,
  // whichever commits first.
  if (row.localDisposition === 'discard-pending') {
    return abort('pending-prayer-audio-discard-pending');
  }
  const disposition = classifyPendingPrayerAudio(row, queueItemId, expectedOwnerId);
  if (disposition.state === 'refused') return abort(disposition.reason);

  // Passage provenance is part of the destination identity. Home recovery
  // accepts only an unanchored row; Scripture recovery requires byte-for-byte
  // equivalent validated frozen anchors. Malformed persisted provenance parks
  // fail-closed instead of being silently stripped onto the wrong Prayer.
  const rowHasAnchor = row.scriptureAnchor !== undefined;
  const prayerHasAnchor = prayer.scriptureAnchor !== undefined;
  const rowAnchor = rowHasAnchor
    ? parseScriptureAnchor(row.scriptureAnchor)
    : null;
  const prayerAnchor = prayerHasAnchor
    ? parseScriptureAnchor(prayer.scriptureAnchor)
    : null;
  if (
    rowHasAnchor !== prayerHasAnchor ||
    (rowHasAnchor &&
      (!rowAnchor ||
        !prayerAnchor ||
        !scriptureAnchorsEqual(rowAnchor, prayerAnchor)))
  ) {
    return abort('pending-prayer-scripture-anchor-mismatch');
  }

  // A byte-bearing row linked while Enhanced Voice is closed cannot be left
  // as ordinary pending work: this build will not process it, and a later
  // voice-enabled build must not silently consume it before the user chooses
  // Retry. Project both halves as terminal recovery state in this same
  // transaction so the Prayer always has the Type-what-you-remember surface.
  const parkedForVoice =
    !enhancedVoiceAllowed() && audioQueueRowCarriesBytes(row);
  const linked = keepVerifiedCaptureCopy(row, {
    ...row,
    prayerId: prayer.id,
    awaitingInlineSave: false,
    failed: row.failed || parkedForVoice,
  }, copy);
  const committedPrayer: Prayer = {
    ...prayer,
    transcriptionFailed:
      disposition.state === 'terminal' || parkedForVoice ? true : undefined,
  };
  // Binding a capture changes prayerId and inline-save state, never its
  // frozen identity or payload; a refusal aborts before anything is queued.
  const captureRefusal = captureRowTransitionRefusal(row, linked);
  if (captureRefusal) return abortForCaptureRefusal(tx, captureRefusal);
  // A failed link (a refused put, a storage failure) aborts the whole
  // transaction and is reported once, by the rejected `tx.done` or the throw
  // from the fence read; nothing is written and the orphan row stays offered.
  // The queued requests and the transaction promise are observed so that the
  // same abort never also surfaces as an unhandled rejection.
  void tx.done.catch(() => {});
  observeQueuedWrite(prayers.put(committedPrayer));
  observeQueuedWrite(audio.put(linked));
  const marks = await bumpFenceAndEpochInTx(
    tx as unknown as KeepsakeWriteTx,
    prayer.id,
    'local',
  );
  await tx.done;
  afterFenceWrite(prayer.id, marks.seq, marks.epoch);
  return { prayer: committedPrayer, row: linked };
}

/**
 * Why `readPendingPrayerLinkProof` refused. Every reason keeps the calling
 * surface's existing failure copy; none of them authorizes a write.
 */
export type PendingPrayerLinkProofRefusal =
  | 'owner-binding'
  | 'row-missing'
  | 'row-owner'
  | 'row-not-new-prayer'
  | 'row-unlinked'
  | 'prayer-missing'
  | 'anchor-mismatch'
  | 'destination-invalid';

export type PendingPrayerLinkProof =
  | { saved: true; row: AudioQueueRow; prayer: Prayer }
  | { saved: false; reason: PendingPrayerLinkProofRefusal };

/**
 * A stale page's duplicate Save: `savePendingPrayerAndLinkAudio` refused with
 * `pending-prayer-audio-already-linked` because another page already linked
 * this exact row. Before any surface reports that Save as already done, this
 * proves from durable state alone, in ONE readonly transaction, that the same
 * owned recording already has its valid destination: the durable binding
 * names the expected owner; the row exists under that owner and names a
 * Prayer; that Prayer exists; the passage provenance equals what this Save
 * would have linked (absent for Home and an unanchored resume card, equal
 * validated anchors for Pray-on-Scripture, on the row and on the Prayer, which
 * carries `null` for an absent anchor once projected from a lifecycle
 * document); the row is a new-prayer recording, the only kind the link
 * transaction links (its recovery kind absent or `new-prayer` and its
 * inline-save source absent or `prayer`); and the relationship is live: the
 * Prayer still names the row as pending (an unresolved row), or the row is
 * bound to that Prayer by its content commit (`destination-commit-pending`).
 * Anything else is a refusal: a missing Prayer, a foreign binding or row, an
 * update, testimony or bookmark recording, a mismatched or malformed anchor, a
 * row whose stored disposition is `discard-pending` (a discard is refused only
 * once that disposition is durable here; audio-queue.ts dequeueAudio records it
 * before its registry request, so a discard in flight is seen too), any
 * other disposition (no client code writes `deleted`, so that value proves
 * nothing), a row bound to another entity, or a Prayer whose pointer was
 * cleared without binding the row (the voice-off typed replacement keeps that
 * row retained but unbound). Reads only; never writes.
 */
export async function readPendingPrayerLinkProof(
  queueItemId: string,
  expectedOwnerId: string,
  expectedScriptureAnchor?: unknown,
): Promise<PendingPrayerLinkProof> {
  const anchorExpected =
    expectedScriptureAnchor !== undefined && expectedScriptureAnchor !== null;
  const expectedAnchor = anchorExpected
    ? parseScriptureAnchor(expectedScriptureAnchor)
    : null;
  if (anchorExpected && !expectedAnchor) {
    return { saved: false, reason: 'anchor-mismatch' };
  }
  const db = await getDb();
  const tx = db.transaction(
    [PRAYERS_STORE, AUDIO_QUEUE_STORE, APP_META_STORE],
    'readonly',
  );
  const binding = await tx.objectStore(APP_META_STORE).get(CONTENT_OWNER_META_KEY);
  const row = await tx.objectStore(AUDIO_QUEUE_STORE).get(queueItemId);
  const prayer =
    typeof row?.prayerId === 'string' && row.prayerId !== ''
      ? await tx.objectStore(PRAYERS_STORE).get(row.prayerId)
      : undefined;
  await tx.done;
  return decidePendingPrayerLinkProof({
    binding: binding?.value,
    row,
    prayer,
    queueItemId,
    expectedOwnerId,
    expectedAnchor,
  });
}

/** The pure decision behind `readPendingPrayerLinkProof`; fails closed. */
function decidePendingPrayerLinkProof(input: {
  binding: unknown;
  row: AudioQueueRow | undefined;
  prayer: Prayer | undefined;
  queueItemId: string;
  expectedOwnerId: string;
  expectedAnchor: ScriptureAnchor | null;
}): PendingPrayerLinkProof {
  const { binding, row, prayer, queueItemId, expectedOwnerId, expectedAnchor } = input;
  if (binding !== expectedOwnerId) return { saved: false, reason: 'owner-binding' };
  if (!row || row.id !== queueItemId) return { saved: false, reason: 'row-missing' };
  if (row.ownerId !== expectedOwnerId) return { saved: false, reason: 'row-owner' };
  // The shapes savePendingPrayerAndLinkAudio links (classifyPendingPrayerAudio):
  // a new-prayer recording from a prayer surface, or a legacy row with neither.
  if (
    (row.recoveryKind != null && row.recoveryKind !== 'new-prayer') ||
    (row.inlineSaveSource != null && row.inlineSaveSource !== 'prayer')
  ) {
    return { saved: false, reason: 'row-not-new-prayer' };
  }
  if (typeof row.prayerId !== 'string' || row.prayerId === '') {
    return { saved: false, reason: 'row-unlinked' };
  }
  if (!prayer || prayer.id !== row.prayerId) return { saved: false, reason: 'prayer-missing' };
  // Passage provenance: the rule the link transaction applied, now against the
  // durable row and Prayer. A malformed stored anchor never equals anything.
  const rowAnchorPresent = row.scriptureAnchor !== undefined;
  const prayerAnchorPresent =
    prayer.scriptureAnchor !== undefined && prayer.scriptureAnchor !== null;
  if (expectedAnchor) {
    const rowAnchor = rowAnchorPresent ? parseScriptureAnchor(row.scriptureAnchor) : null;
    const prayerAnchor = prayerAnchorPresent
      ? parseScriptureAnchor(prayer.scriptureAnchor)
      : null;
    if (
      !rowAnchor ||
      !prayerAnchor ||
      !scriptureAnchorsEqual(rowAnchor, expectedAnchor) ||
      !scriptureAnchorsEqual(prayerAnchor, expectedAnchor)
    ) {
      return { saved: false, reason: 'anchor-mismatch' };
    }
  } else if (rowAnchorPresent || prayerAnchorPresent) {
    return { saved: false, reason: 'anchor-mismatch' };
  }
  const stillPending =
    prayer.pendingTranscriptionId === row.id && row.localDisposition === 'unresolved';
  const bound =
    row.localDisposition === 'destination-commit-pending' &&
    row.destinationEntityType === 'prayer' &&
    row.destinationEntityId === row.prayerId;
  if (!stillPending && !bound) return { saved: false, reason: 'destination-invalid' };
  return { saved: true, row, prayer };
}

export async function deletePrayer(id: string): Promise<void> {
  assertDbWrite('db:prayer:delete');
  const db = await getDb();
  const tx = db.transaction(
    [PRAYERS_STORE, KEEPSAKE_FENCES_STORE, KEEPSAKE_META_STORE],
    'readwrite',
  );
  void tx.objectStore(PRAYERS_STORE).delete(id);
  // A physically deleted local prayer removes its fence sidecar; the
  // validity-intent journal (keepsake-intents) is what continues to
  // represent the deletion (PC-B 10b.2). The epoch still advances.
  void tx.objectStore(KEEPSAKE_FENCES_STORE).delete(id);
  const meta = tx.objectStore(KEEPSAKE_META_STORE);
  const epochRow = await meta.get(KEEPSAKE_EPOCH_KEY);
  const nextEpoch = decimalIncrement(
    epochRow && typeof (epochRow as { value?: unknown }).value === 'string'
      ? (epochRow as { value: string }).value
      : '0',
  );
  void meta.put({ key: 'local-prayer-epoch', value: nextEpoch });
  await tx.done;
  epochBroadcast?.(nextEpoch);
}

// ---- Guarded read-modify-write primitives (offline-first Chunk 2) ----
//
// Both run the read + decision + write inside ONE IndexedDB `readwrite`
// transaction, with NO non-IDB `await` between the get and the put, so the
// transaction stays open (idb auto-commits only when no further request is
// pending) and the whole operation is atomic against EVERY other IDB writer
// to the prayers store — user edits, the pushAndMark sync stamp, AND the
// syncPrayers pull. A JS write-chain only serializes other chained writes, so
// it cannot protect against the sync pull (which writes IDB directly before
// loadPrayers refreshes the store); IDB transaction isolation can. See plan
// §3.1a. `guard` and `computeNext` MUST be synchronous (no await) or the
// transaction auto-commits before the put.

/**
 * Re-read the live prayer row, run `guard` against it, and write
 * `computeNext(row)` only if the guard passes — all in one transaction.
 * Returns the written row, or null if the row is gone or the guard fails
 * (so a concurrent newer write that landed since the caller read is seen and
 * the write aborts). Used by the enrichment apply path.
 */
export async function guardedPatchPrayer(
  id: string,
  guard: (row: Prayer) => boolean,
  computeNext: (row: Prayer) => Prayer,
): Promise<Prayer | null> {
  assertDbWrite('db:prayer:patch');
  const db = await getDb();
  const tx = db.transaction(
    [PRAYERS_STORE, KEEPSAKE_FENCES_STORE, KEEPSAKE_META_STORE],
    'readwrite',
  );
  const prayers = tx.objectStore(PRAYERS_STORE);
  const row = await prayers.get(id);
  if (!row || !guard(row)) {
    await tx.done;
    return null;
  }
  const next = computeNext(row);
  void prayers.put(next);
  let marks: { seq: string; epoch: string } | null = null;
  if (renderRelevantChanged(row, next)) {
    marks = await bumpFenceAndEpochInTx(tx as unknown as KeepsakeWriteTx, id, 'local');
  }
  await tx.done;
  if (marks) afterFenceWrite(id, marks.seq, marks.epoch);
  return next;
}

/**
 * Apply a pulled remote row (or deletion) to the live local row, deciding
 * overwrite-vs-skip against the CURRENT row read inside the transaction (NOT
 * a once-captured snapshot — codex pass-7). `remote === null` is a remote
 * deletion; `remoteUpdatedAt` is the remote row's updated_at. Whole-row LWW:
 * a strictly-newer remote wins; an equal-or-older remote is skipped so a
 * newer local edit survives and re-pushes. Does NOT touch the rule-#42 PUSH
 * criterion — this is pull-side freshness only.
 */
export async function applyPulledPrayer(
  id: string,
  remote: Prayer | null,
  remoteUpdatedAt: string,
): Promise<'saved' | 'deleted' | 'skipped'> {
  assertDbWrite('db:prayer:pull-apply');
  const db = await getDb();
  const tx = db.transaction(
    [PRAYERS_STORE, KEEPSAKE_FENCES_STORE, KEEPSAKE_META_STORE],
    'readwrite',
  );
  const prayers = tx.objectStore(PRAYERS_STORE);
  const current = await prayers.get(id);
  let outcome: 'saved' | 'deleted' | 'skipped' = 'skipped';
  let marks: { seq: string; epoch: string } | null = null;
  if (remote === null) {
    if (current && remoteUpdatedAt > current.updatedAt) {
      void prayers.delete(id);
      void tx.objectStore(KEEPSAKE_FENCES_STORE).delete(id);
      outcome = 'deleted';
    }
  } else if (!current || remoteUpdatedAt > current.updatedAt) {
    void prayers.put({ ...remote, syncedAt: remoteUpdatedAt });
    // A pulled row IS server state: both fence sequences advance
    // together (already acknowledged), so a pull never strands the
    // keepsake gate open-ended.
    marks = await bumpFenceAndEpochInTx(tx as unknown as KeepsakeWriteTx, id, 'pulled');
    outcome = 'saved';
  }
  await tx.done;
  if (marks) afterFenceWrite(id, marks.seq, marks.epoch);
  return outcome;
}

// ---- Durable cloud-account sentinel (offline-first Chunk 2) ----
//
// The IDB-domain source of truth for "this device has ever had a cloud
// account." Lives in the same store family as the prayer rows it gates (see
// the v7 upgrade clause). Written on session observation by markCloudAccount
// (sync-events.ts); read by the boot seed. NEVER cleared.

export async function getCloudAccountFlag(): Promise<boolean> {
  const db = await getDb();
  const row = await db.get(APP_META_STORE, CLOUD_ACCOUNT_META_KEY);
  return row?.value === true;
}

export async function setCloudAccountFlag(): Promise<void> {
  assertDbWrite('db:app-meta:cloud-account');
  const db = await getDb();
  await db.put(APP_META_STORE, { key: CLOUD_ACCOUNT_META_KEY, value: true });
}

// ---- Durable content-owner binding (core-content cutover) ----
//
// The second app-meta row family. Records WHICH account this browser
// profile's local content belongs to, so a different session (or an
// unbound legacy profile) can never display or sync it. Policy lives in
// src/lib/content-owner.ts; these are the raw accessors.
//
// The write is guarded like every other content write, so in practice it
// only lands inside `__dangerouslyRunRestoreInstall` (the operator
// restore). content-owner.ts additionally requires the bypass to be
// active, so an ordinary caller cannot bind an owner even in a
// writes-enabled build.

export const CONTENT_OWNER_META_KEY = 'content-owner';
const OWNER_ADOPTION_CLEANUP_META_KEY = 'owner-adoption-cleanup';

export async function getContentOwnerBindingRow(): Promise<string | null> {
  const db = await getDb();
  const row = await db.get(APP_META_STORE, CONTENT_OWNER_META_KEY);
  return typeof row?.value === 'string' && row.value !== '' ? row.value : null;
}

export async function setContentOwnerBindingRow(ownerId: string): Promise<void> {
  assertDbWrite('db:app-meta:content-owner');
  const db = await getDb();
  await db.put(APP_META_STORE, { key: CONTENT_OWNER_META_KEY, value: ownerId });
}

/**
 * A successful authoritative install writes this marker in the same
 * transaction as the new owner binding. The React gate clears owner-specific
 * localStorage and in-memory projections before removing it. A reload between
 * those two steps therefore stays closed instead of exposing stale browser
 * state to the newly bound account.
 */
export async function getOwnerAdoptionCleanupRow(): Promise<string | null> {
  const db = await getDb();
  const row = await db.get(APP_META_STORE, OWNER_ADOPTION_CLEANUP_META_KEY);
  return typeof row?.value === 'string' && row.value !== '' ? row.value : null;
}

/** Complete only the narrow post-install cleanup handshake. */
export async function completeOwnerAdoptionCleanupRow(ownerId: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(APP_META_STORE, 'readwrite');
  const [binding, cleanup] = await Promise.all([
    tx.store.get(CONTENT_OWNER_META_KEY),
    tx.store.get(OWNER_ADOPTION_CLEANUP_META_KEY),
  ]);
  if (binding?.value !== ownerId || cleanup?.value !== ownerId) {
    tx.abort();
    await tx.done.catch(() => {});
    throw new Error('owner-adoption-cleanup-mismatch');
  }
  void tx.store.delete(OWNER_ADOPTION_CLEANUP_META_KEY);
  await tx.done;
}

export type EmptyProfileOwnerBindResult =
  | 'bound'
  | 'already-bound'
  | 'unsafe';

// Owner-bearing stores that must participate in the empty-profile bind
// transaction. BSB books/meta are intentionally absent: they are public,
// re-downloadable scripture caches. Every store that can carry authored
// content, recovery state, an owner frontier, or a prior-account signal is
// locked and checked before the binding lands.
const EMPTY_PROFILE_OWNER_GUARD_STORES = [
  PRAYERS_STORE,
  AUDIO_QUEUE_STORE,
  BIBLE_BOOKMARKS_STORE,
  BIBLE_HIGHLIGHTS_STORE,
  APP_META_STORE,
  KEEPSAKE_FENCES_STORE,
  KEEPSAKE_INTENTS_STORE,
  KEEPSAKE_META_STORE,
  KEEPSAKE_PINS_STORE,
  CONTENT_ENTITIES_STORE,
  CONTENT_HOLDING_STORE,
  CONTENT_PENDING_STORE,
  CONTENT_PARTITIONS_STORE,
  CONTENT_STAGED_CONFLICTS_STORE,
  CONTENT_CONFLICTS_STORE,
  CONTENT_OBSERVATIONS_STORE,
] as const;

const EMPTY_PROFILE_ZERO_ROW_STORES = EMPTY_PROFILE_OWNER_GUARD_STORES.filter(
  (name) => name !== APP_META_STORE && name !== KEEPSAKE_META_STORE,
);

function isFreshKeepsakeMeta(
  rows: Array<
    | { key: 'local-prayer-epoch'; value: string }
    | { key: 'corpus-fence'; fence: LocalPrayerCorpusFenceV1 }
  >,
): boolean {
  // A v8 -> v10 upgrade creates exactly this inert epoch row even when the
  // profile has never held a prayer. Any other row/value proves prior local
  // lifecycle activity and must keep the recovery gate closed.
  return rows.length === 0 || (
    rows.length === 1 &&
    rows[0].key === KEEPSAKE_EPOCH_KEY &&
    rows[0].value === '1' &&
    JSON.stringify(Object.keys(rows[0]).sort()) === JSON.stringify(['key', 'value'])
  );
}

const OWNER_GUARD_STORE_MIN_VERSION: Record<
  (typeof EMPTY_PROFILE_OWNER_GUARD_STORES)[number],
  number
> = {
  [PRAYERS_STORE]: 1,
  [AUDIO_QUEUE_STORE]: 2,
  [BIBLE_BOOKMARKS_STORE]: 6,
  [APP_META_STORE]: 7,
  [BIBLE_HIGHLIGHTS_STORE]: 8,
  [KEEPSAKE_FENCES_STORE]: 9,
  [KEEPSAKE_INTENTS_STORE]: 9,
  [KEEPSAKE_META_STORE]: 9,
  [KEEPSAKE_PINS_STORE]: 9,
  [CONTENT_ENTITIES_STORE]: 10,
  [CONTENT_HOLDING_STORE]: 10,
  [CONTENT_PENDING_STORE]: 10,
  [CONTENT_PARTITIONS_STORE]: 10,
  [CONTENT_STAGED_CONFLICTS_STORE]: 10,
  [CONTENT_CONFLICTS_STORE]: 10,
  [CONTENT_OBSERVATIONS_STORE]: 10,
};

export type UnboundContentOwnerProfile = 'empty' | 'protected' | 'unreadable';

/**
 * Read-only half of the empty-profile proof. It supports every shipped schema
 * version and never upgrades or writes the database. Any missing expected
 * store, unknown metadata or lifecycle sidecar stays protected. A read that
 * fails inside the inspection is `unreadable`: nothing was found, and nothing
 * was proven empty either. An open failure still rejects. Neither is ever
 * treated as empty (content-owner.ts maps both to its fail-closed results).
 */
export async function inspectUnboundContentOwnerProfileRow(): Promise<
  UnboundContentOwnerProfile
> {
  const db = await getDb();
  const expectedStores = EMPTY_PROFILE_OWNER_GUARD_STORES.filter(
    (name) => OWNER_GUARD_STORE_MIN_VERSION[name] <= db.version,
  );
  if (expectedStores.some((name) => !db.objectStoreNames.contains(name))) {
    return 'protected';
  }
  const tx = db.transaction(expectedStores, 'readonly');
  try {
    const zeroRowStores = expectedStores.filter(
      (name) => name !== APP_META_STORE && name !== KEEPSAKE_META_STORE,
    );
    const [appMetaKeys, keepsakeMetaRows, zeroRowCounts] = await Promise.all([
      db.objectStoreNames.contains(APP_META_STORE)
        ? tx.objectStore(APP_META_STORE).getAllKeys()
        : Promise.resolve([]),
      db.objectStoreNames.contains(KEEPSAKE_META_STORE)
        ? tx.objectStore(KEEPSAKE_META_STORE).getAll()
        : Promise.resolve([]),
      Promise.all(zeroRowStores.map((name) => tx.objectStore(name).count())),
    ]);
    await tx.done;
    if (
      appMetaKeys.some((key) => key !== CLOUD_ACCOUNT_META_KEY) ||
      !isFreshKeepsakeMeta(keepsakeMetaRows) ||
      zeroRowCounts.some((count) => count !== 0)
    ) return 'protected';
    return 'empty';
  } catch {
    await tx.done.catch(() => {});
    return 'unreadable';
  }
}

/**
 * Bind a genuinely empty writable v10 profile to one authenticated owner.
 *
 * This is NOT a general binding setter and cannot migrate legacy bytes. The
 * read/write transaction locks every owner-bearing store, proves it empty
 * (apart from the inert v9 epoch seed), and writes the owner binding plus the
 * cloud-account sentinel together. `stillAuthorizedAndLocallyEmpty` is a
 * synchronous external guard supplied by content-owner.ts; it binds the
 * transaction to the still-current auth session and to localStorage's
 * separate recovery-content boundary immediately before commit.
 *
 * Any existing binding, content, pending tuple, conflict, unknown metadata,
 * unexpected schema version, or guard change returns `unsafe` without
 * committing a byte. In particular, this path never replaces a known owner:
 * one browser profile belongs to one account in this release.
 */
export async function bindEmptyContentOwnerRowAtomically(
  requestedOwnerId: string,
  stillAuthorizedAndLocallyEmpty: () => boolean,
): Promise<EmptyProfileOwnerBindResult> {
  assertDbWrite('db:app-meta:empty-profile-owner');
  const ownerId = requestedOwnerId.trim();
  if (ownerId === '') throw new Error('empty-profile-owner-required');
  if (!stillAuthorizedAndLocallyEmpty()) return 'unsafe';

  const db = await getDb();
  if (db.version !== DB_VERSION) return 'unsafe';
  if (EMPTY_PROFILE_OWNER_GUARD_STORES.some(
    (name) => !db.objectStoreNames.contains(name),
  )) return 'unsafe';

  const tx = db.transaction(EMPTY_PROFILE_OWNER_GUARD_STORES, 'readwrite');
  const refuse = async (): Promise<'unsafe'> => {
    try {
      tx.abort();
    } catch {
      // It may already have aborted after a failed request.
    }
    await tx.done.catch(() => {});
    return 'unsafe';
  };

  try {
    if (!stillAuthorizedAndLocallyEmpty()) return await refuse();
    const appMeta = tx.objectStore(APP_META_STORE);
    const existingBinding = await appMeta.get(CONTENT_OWNER_META_KEY);
    if (existingBinding?.value === ownerId) {
      if (
        stillAuthorizedAndLocallyEmpty()
      ) {
        await tx.done;
        return 'already-bound';
      }
      return await refuse();
    }
    if (existingBinding !== undefined) return await refuse();

    const [appMetaKeys, keepsakeMetaRows, zeroRowCounts] = await Promise.all([
      appMeta.getAllKeys(),
      tx.objectStore(KEEPSAKE_META_STORE).getAll(),
      Promise.all(
        EMPTY_PROFILE_ZERO_ROW_STORES.map((name) =>
          tx.objectStore(name).count(),
        ),
      ),
    ]);
    if (
      appMetaKeys.some(
        (key) => key !== CONTENT_OWNER_META_KEY && key !== CLOUD_ACCOUNT_META_KEY,
      ) ||
      !isFreshKeepsakeMeta(keepsakeMetaRows) ||
      zeroRowCounts.some((count) => count !== 0) ||
      !stillAuthorizedAndLocallyEmpty()
    ) return await refuse();

    await Promise.all([
      appMeta.put({ key: CONTENT_OWNER_META_KEY, value: ownerId }),
      appMeta.put({ key: CLOUD_ACCOUNT_META_KEY, value: true }),
    ]);
    const finalMetaKeys = await appMeta.getAllKeys();
    if (
      JSON.stringify(sortedStringKeys(finalMetaKeys)) !==
        JSON.stringify([CLOUD_ACCOUNT_META_KEY, CONTENT_OWNER_META_KEY].sort()) ||
      !stillAuthorizedAndLocallyEmpty()
    ) return await refuse();

    await tx.done;
    return 'bound';
  } catch (error) {
    try {
      tx.abort();
    } catch {
      // It may already have completed or auto-aborted.
    }
    void tx.done.catch(() => {});
    throw error;
  }
}

export type UnboundOwnerMatchCandidate = {
  prayers: Prayer[];
  bookmarks: BibleBookmark[];
  highlights: BibleHighlight[];
};

function stablePlainString(value: unknown): string {
  if (value === undefined) return '"$undefined"';
  if (value === null || typeof value === 'string' || typeof value === 'boolean') {
    return JSON.stringify(value);
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new Error('owner-match-nonfinite');
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stablePlainString).join(',')}]`;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record).sort().map(
      (key) => `${JSON.stringify(key)}:${stablePlainString(record[key])}`,
    ).join(',')}}`;
  }
  throw new Error('owner-match-unrepresentable');
}

function ownerMatchCandidateEqual(
  left: UnboundOwnerMatchCandidate,
  right: UnboundOwnerMatchCandidate,
): boolean {
  return stablePlainString(left) === stablePlainString(right);
}

/** Read the three active ordinary projections for a server ownership proof. */
export async function getUnboundOwnerMatchCandidate(): Promise<
  UnboundOwnerMatchCandidate | null
> {
  const db = await getDb();
  if (db.version !== DB_VERSION) return null;
  const lifecycleStores = [
    CONTENT_ENTITIES_STORE,
    CONTENT_HOLDING_STORE,
    CONTENT_PENDING_STORE,
    CONTENT_PARTITIONS_STORE,
    CONTENT_STAGED_CONFLICTS_STORE,
    CONTENT_CONFLICTS_STORE,
    CONTENT_OBSERVATIONS_STORE,
  ] as const;
  const tx = db.transaction(
    [
      PRAYERS_STORE,
      AUDIO_QUEUE_STORE,
      BIBLE_BOOKMARKS_STORE,
      BIBLE_HIGHLIGHTS_STORE,
      APP_META_STORE,
      ...lifecycleStores,
    ],
    'readonly',
  );
  const [appMetaKeys, binding, audioCount, prayers, bookmarks, highlights, lifecycleCounts] =
    await Promise.all([
      tx.objectStore(APP_META_STORE).getAllKeys(),
      tx.objectStore(APP_META_STORE).get(CONTENT_OWNER_META_KEY),
      tx.objectStore(AUDIO_QUEUE_STORE).count(),
      tx.objectStore(PRAYERS_STORE).getAll(),
      tx.objectStore(BIBLE_BOOKMARKS_STORE).getAll(),
      tx.objectStore(BIBLE_HIGHLIGHTS_STORE).getAll(),
      Promise.all(lifecycleStores.map((name) => tx.objectStore(name).count())),
    ]);
  await tx.done;
  if (
    binding !== undefined ||
    appMetaKeys.some((key) => key !== CLOUD_ACCOUNT_META_KEY) ||
    audioCount !== 0 ||
    lifecycleCounts.some((count) => count !== 0)
  ) return null;
  if (prayers.length + bookmarks.length + highlights.length === 0) return null;
  return { prayers, bookmarks, highlights };
}

/**
 * Install a binding only after the caller has proved the exact active
 * projections are a subset of this authenticated owner's server history.
 * The transaction re-reads the candidate and all active safety sidecars so a
 * changed draft/audio/projection or a different owner witness aborts.
 */
export async function bindMatchedContentOwnerRowAtomically(
  ownerId: string,
  expected: UnboundOwnerMatchCandidate,
  stillAuthorizedAndLocallyMatchable: () => boolean,
): Promise<boolean> {
  assertDbWrite('db:app-meta:matched-owner');
  if (!stillAuthorizedAndLocallyMatchable()) return false;
  const db = await getDb();
  if (db.version !== DB_VERSION) return false;
  const stores = [
    PRAYERS_STORE,
    AUDIO_QUEUE_STORE,
    BIBLE_BOOKMARKS_STORE,
    BIBLE_HIGHLIGHTS_STORE,
    APP_META_STORE,
    KEEPSAKE_FENCES_STORE,
    KEEPSAKE_INTENTS_STORE,
    KEEPSAKE_META_STORE,
    KEEPSAKE_PINS_STORE,
    CONTENT_ENTITIES_STORE,
    CONTENT_HOLDING_STORE,
    CONTENT_PENDING_STORE,
    CONTENT_PARTITIONS_STORE,
    CONTENT_STAGED_CONFLICTS_STORE,
    CONTENT_CONFLICTS_STORE,
    CONTENT_OBSERVATIONS_STORE,
  ] as const;
  const tx = db.transaction(stores, 'readwrite');
  const abort = async (): Promise<boolean> => {
    try {
      tx.abort();
    } catch {
      // It may already have aborted.
    }
    await tx.done.catch(() => {});
    return false;
  };
  try {
    const [
      binding,
      audioCount,
      prayers,
      bookmarks,
      highlights,
      fences,
      intents,
      meta,
      pins,
      lifecycleCounts,
      appMetaKeys,
    ] = await Promise.all([
      tx.objectStore(APP_META_STORE).get(CONTENT_OWNER_META_KEY),
      tx.objectStore(AUDIO_QUEUE_STORE).count(),
      tx.objectStore(PRAYERS_STORE).getAll(),
      tx.objectStore(BIBLE_BOOKMARKS_STORE).getAll(),
      tx.objectStore(BIBLE_HIGHLIGHTS_STORE).getAll(),
      tx.objectStore(KEEPSAKE_FENCES_STORE).getAll(),
      tx.objectStore(KEEPSAKE_INTENTS_STORE).getAll(),
      tx.objectStore(KEEPSAKE_META_STORE).getAll(),
      tx.objectStore(KEEPSAKE_PINS_STORE).getAll(),
      Promise.all([
        tx.objectStore(CONTENT_ENTITIES_STORE).count(),
        tx.objectStore(CONTENT_HOLDING_STORE).count(),
        tx.objectStore(CONTENT_PENDING_STORE).count(),
        tx.objectStore(CONTENT_PARTITIONS_STORE).count(),
        tx.objectStore(CONTENT_STAGED_CONFLICTS_STORE).count(),
        tx.objectStore(CONTENT_CONFLICTS_STORE).count(),
        tx.objectStore(CONTENT_OBSERVATIONS_STORE).count(),
      ]),
      tx.objectStore(APP_META_STORE).getAllKeys(),
    ]);
    const current = { prayers, bookmarks, highlights };
    const differentOwnerWitness =
      fences.some((row) => row.ownerScope !== null && row.ownerScope !== ownerId) ||
      intents.some((row) => row.ownerScope !== ownerId) ||
      meta.some(
        (row) => 'fence' in row && row.fence.ownerScope !== ownerId,
      ) ||
      pins.some((row) => row.pin.ownerId !== ownerId);
    if (
      binding !== undefined ||
      appMetaKeys.some((key) => key !== CLOUD_ACCOUNT_META_KEY) ||
      audioCount !== 0 ||
      lifecycleCounts.some((count) => count !== 0) ||
      differentOwnerWitness ||
      !ownerMatchCandidateEqual(current, expected) ||
      !stillAuthorizedAndLocallyMatchable()
    ) return await abort();

    const appMeta = tx.objectStore(APP_META_STORE);
    await Promise.all([
      appMeta.put({ key: CONTENT_OWNER_META_KEY, value: ownerId }),
      appMeta.put({ key: CLOUD_ACCOUNT_META_KEY, value: true }),
    ]);
    const finalMetaKeys = await appMeta.getAllKeys();
    if (
      JSON.stringify(sortedStringKeys(finalMetaKeys)) !==
        JSON.stringify([CLOUD_ACCOUNT_META_KEY, CONTENT_OWNER_META_KEY].sort()) ||
      !stillAuthorizedAndLocallyMatchable()
    ) return await abort();
    await tx.done;
    return true;
  } catch (error) {
    try {
      tx.abort();
    } catch {
      // It may already have completed or auto-aborted.
    }
    void tx.done.catch(() => {});
    throw error;
  }
}

export interface AuthoritativeOwnerWorkspaceInstall {
  ownerId: string;
  highWater: number;
  prayers: Prayer[];
  bookmarks: BibleBookmark[];
  highlights: BibleHighlight[];
  entities: LocalContentEntityV1[];
}

const AUTHORITATIVE_OWNER_WORKSPACE_STORES = [
  PRAYERS_STORE,
  AUDIO_QUEUE_STORE,
  BIBLE_BOOKMARKS_STORE,
  BIBLE_HIGHLIGHTS_STORE,
  APP_META_STORE,
  KEEPSAKE_FENCES_STORE,
  KEEPSAKE_INTENTS_STORE,
  KEEPSAKE_META_STORE,
  KEEPSAKE_PINS_STORE,
  CONTENT_ENTITIES_STORE,
  CONTENT_HOLDING_STORE,
  CONTENT_PENDING_STORE,
  CONTENT_PARTITIONS_STORE,
  CONTENT_STAGED_CONFLICTS_STORE,
  CONTENT_CONFLICTS_STORE,
  CONTENT_OBSERVATIONS_STORE,
] as const;

function legacyProjectionHasPendingWork(
  prayers: Prayer[],
  bookmarks: BibleBookmark[],
  highlights: BibleHighlight[],
): boolean {
  return prayers.some(
    (row) => Boolean(row.pendingTranscriptionId) || row.syncedAt !== row.updatedAt,
  ) || bookmarks.some((row) => row.syncedAt !== row.updatedAt) ||
    highlights.some((row) => row.syncedAt !== row.updatedAt);
}

async function hasPositiveUnsyncedOwnerWorkInTx(
  tx: ReturnType<Awaited<ReturnType<typeof getDb>>['transaction']>,
  ownerId: string,
  includeLifecycle: boolean,
): Promise<boolean> {
  const attemptKeys = await tx.objectStore(APP_META_STORE).getAllKeys();
  if (attemptKeys.some(key => typeof key === 'string' && key.startsWith('prayer-authoring-attempt-v1:'))) return true;
  const [audioCount, prayers, bookmarks, highlights] = await Promise.all([
    tx.objectStore(AUDIO_QUEUE_STORE).count(),
    tx.objectStore(PRAYERS_STORE).getAll(),
    tx.objectStore(BIBLE_BOOKMARKS_STORE).getAll(),
    tx.objectStore(BIBLE_HIGHLIGHTS_STORE).getAll(),
  ]);
  if (audioCount > 0 || prayers.some((row) => Boolean(row.pendingTranscriptionId))) {
    return true;
  }
  if (!includeLifecycle) {
    return legacyProjectionHasPendingWork(prayers, bookmarks, highlights);
  }

  const [partition, entities, pending, staged, conflicts, fences, intents] =
    await Promise.all([
      tx.objectStore(CONTENT_PARTITIONS_STORE).get(ownerId),
      tx.objectStore(CONTENT_ENTITIES_STORE).getAll(),
      tx.objectStore(CONTENT_PENDING_STORE).getAll(),
      tx.objectStore(CONTENT_STAGED_CONFLICTS_STORE).getAll(),
      tx.objectStore(CONTENT_CONFLICTS_STORE).getAll(),
      tx.objectStore(KEEPSAKE_FENCES_STORE).getAll(),
      tx.objectStore(KEEPSAKE_INTENTS_STORE).getAll(),
    ]);
  if (
    pending.some(
      (row) => row.ownerId === ownerId && row.dispatchState !== 'acked-superseded',
    ) ||
    staged.some((row) => row.ownerId === ownerId) ||
    conflicts.some((row) => row.ownerId === ownerId) ||
    entities.some(
      (row) => row.ownerId === ownerId &&
        (row.pending !== null || row.state === 'dirty' || row.state === 'conflicted'),
    ) ||
    intents.some((row) => row.ownerScope === ownerId && row.state !== 'acknowledged') ||
    fences.some(
      (row) =>
        row.inFlightMutationSeq !== null ||
        row.localMutationSeq !== row.acknowledgedMutationSeq,
    )
  ) {
    return true;
  }
  return partition?.legacyBootstrapComplete !== true &&
    legacyProjectionHasPendingWork(prayers, bookmarks, highlights);
}

/**
 * Positive, transactionally consistent unsynced-work check for account
 * switching. It does not infer pending work from dates when an initialized
 * lifecycle partition exists. Legacy profiles retain their shipped
 * `syncedAt === updatedAt` outbox criterion until adopted once.
 */
export async function hasPositiveUnsyncedOwnerWorkRow(ownerId: string): Promise<boolean> {
  const db = await getDb();
  if (db.version < 8 || db.version > DB_VERSION) return true;
  const includeLifecycle = db.version >= DB_VERSION;
  const stores = includeLifecycle
    ? AUTHORITATIVE_OWNER_WORKSPACE_STORES
    : [
        PRAYERS_STORE,
        AUDIO_QUEUE_STORE,
        BIBLE_BOOKMARKS_STORE,
        BIBLE_HIGHLIGHTS_STORE,
        APP_META_STORE,
      ] as const;
  const tx = db.transaction(stores, 'readonly');
  try {
    const binding = await tx.objectStore(APP_META_STORE).get(CONTENT_OWNER_META_KEY);
    const pending = binding?.value !== ownerId ||
      await hasPositiveUnsyncedOwnerWorkInTx(
        tx as unknown as Parameters<typeof hasPositiveUnsyncedOwnerWorkInTx>[0],
        ownerId,
        includeLifecycle,
      );
    await tx.done;
    return pending;
  } catch {
    await tx.done.catch(() => {});
    return true;
  }
}

function sortedEntityKeys(rows: LocalContentEntityV1[]): string[] {
  return rows.map(
    (row) => `${row.ownerId}\n${row.entityType}\n${row.entityId}`,
  ).sort();
}

function actualEntityKeys(keys: IDBValidKey[]): string[] {
  return keys.map((key) => {
    if (
      !Array.isArray(key) || key.length !== 3 ||
      !key.every((item) => typeof item === 'string')
    ) throw new Error('owner-workspace-entity-key-invalid');
    return `${key[0]}\n${key[1]}\n${key[2]}`;
  }).sort();
}

function sameStrings(left: string[], right: string[]): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

/**
 * Install one authenticated server baseline in a single v10 transaction.
 *
 * This is the sole narrow local-write carve-out used by the owner gate while
 * the general client is read-only. It performs no network mutation and cannot
 * author a lifecycle tuple. The complete server response is validated and
 * folded before this function is called. Every owner-bearing store is then
 * cleared, the clean frontier/projections are installed, and the binding plus
 * cleanup marker land together. Any failure aborts all of those requests.
 */
export async function installAuthoritativeOwnerWorkspaceAtomically(
  replacement: AuthoritativeOwnerWorkspaceInstall,
  options: {
    expectedPreviousOwnerId: string | null;
    requireNoUnsyncedOwnerId?: string;
    stillAuthorizedAndSafe: () => boolean;
    abortSignal?: AbortSignal;
    testInterruptAfterClear?: boolean;
  },
): Promise<void> {
  const ownerId = replacement.ownerId.trim();
  if (ownerId === '' || !Number.isSafeInteger(replacement.highWater) || replacement.highWater < 0) {
    throw new Error('owner-workspace-install-invalid');
  }
  if (!options.stillAuthorizedAndSafe() || options.abortSignal?.aborted) {
    throw new Error('owner-workspace-install-authorization-revoked');
  }
  const expectedEntityKeys = sortedEntityKeys(replacement.entities);
  if (
    new Set(expectedEntityKeys).size !== expectedEntityKeys.length ||
    replacement.entities.some(
      (row) => row.ownerId !== ownerId || row.pending !== null ||
        row.installedEnvelope?.ownerId !== ownerId ||
        row.installedEnvelope.contentRevision > replacement.highWater,
    )
  ) throw new Error('owner-workspace-install-entity-invalid');

  const db = await getDb();
  if (db.version !== DB_VERSION || AUTHORITATIVE_OWNER_WORKSPACE_STORES.some(
    (name) => !db.objectStoreNames.contains(name),
  )) throw new Error('owner-workspace-install-requires-v10');

  const tx = db.transaction(AUTHORITATIVE_OWNER_WORKSPACE_STORES, 'readwrite');
  const abort = (): void => {
    try {
      tx.abort();
    } catch {
      // The transaction may already have committed or auto-aborted.
    }
  };
  options.abortSignal?.addEventListener('abort', abort, { once: true });
  const assertSafe = (): void => {
    if (options.abortSignal?.aborted || !options.stillAuthorizedAndSafe()) {
      throw new Error('owner-workspace-install-authorization-revoked');
    }
  };
  try {
    assertSafe();
    const currentBinding = await tx.objectStore(APP_META_STORE).get(CONTENT_OWNER_META_KEY);
    const actualPreviousOwner = typeof currentBinding?.value === 'string'
      ? currentBinding.value
      : null;
    if (actualPreviousOwner !== options.expectedPreviousOwnerId) {
      throw new Error('owner-workspace-install-binding-changed');
    }
    if (
      options.requireNoUnsyncedOwnerId &&
      await hasPositiveUnsyncedOwnerWorkInTx(
        tx as unknown as Parameters<typeof hasPositiveUnsyncedOwnerWorkInTx>[0],
        options.requireNoUnsyncedOwnerId,
        true,
      )
    ) throw new Error('owner-switch-pending-work');
    assertSafe();

    await Promise.all(
      AUTHORITATIVE_OWNER_WORKSPACE_STORES.map((name) => tx.objectStore(name).clear()),
    );
    if (options.testInterruptAfterClear) {
      if (!isNodeProbeRuntime()) throw new Error('owner-workspace-test-interrupt-refused');
      throw new Error('owner-workspace-test-interrupt');
    }

    await Promise.all([
      ...replacement.prayers.map((row) => tx.objectStore(PRAYERS_STORE).put(row)),
      ...replacement.bookmarks.map((row) => tx.objectStore(BIBLE_BOOKMARKS_STORE).put(row)),
      ...replacement.highlights.map((row) => tx.objectStore(BIBLE_HIGHLIGHTS_STORE).put(row)),
      ...replacement.entities.map((row) => tx.objectStore(CONTENT_ENTITIES_STORE).put(row)),
      ...replacement.prayers.map((row) => tx.objectStore(KEEPSAKE_FENCES_STORE).put({
        prayerId: row.id,
        ownerScope: ownerId,
        localMutationSeq: '1',
        acknowledgedMutationSeq: '1',
        inFlightMutationSeq: null,
        payloadDigest: null,
        authEpochAtMutation: 0,
      } satisfies LocalPrayerMutationFenceV1)),
      tx.objectStore(KEEPSAKE_META_STORE).put({ key: KEEPSAKE_EPOCH_KEY, value: '1' }),
      tx.objectStore(CONTENT_PARTITIONS_STORE).put({
        ownerId,
        frontier: replacement.highWater,
        lastObservedHighWater: replacement.highWater,
        legacyBootstrapComplete: true,
        heldReason: null,
        quarantineReason: null,
      } satisfies ContentPartitionV1),
      tx.objectStore(APP_META_STORE).put({ key: CONTENT_OWNER_META_KEY, value: ownerId }),
      tx.objectStore(APP_META_STORE).put({ key: CLOUD_ACCOUNT_META_KEY, value: true }),
      tx.objectStore(APP_META_STORE).put({
        key: OWNER_ADOPTION_CLEANUP_META_KEY,
        value: ownerId,
      }),
    ]);
    assertSafe();

    const [prayerKeys, audioKeys, bookmarkKeys, highlightKeys, entityKeys,
      holdingKeys, pendingKeys, partitionKeys, stagedKeys, conflictKeys,
      observationKeys, fenceKeys, intentKeys, keepsakeMetaKeys, pinKeys, appMetaKeys] =
      await Promise.all([
        tx.objectStore(PRAYERS_STORE).getAllKeys(),
        tx.objectStore(AUDIO_QUEUE_STORE).getAllKeys(),
        tx.objectStore(BIBLE_BOOKMARKS_STORE).getAllKeys(),
        tx.objectStore(BIBLE_HIGHLIGHTS_STORE).getAllKeys(),
        tx.objectStore(CONTENT_ENTITIES_STORE).getAllKeys(),
        tx.objectStore(CONTENT_HOLDING_STORE).getAllKeys(),
        tx.objectStore(CONTENT_PENDING_STORE).getAllKeys(),
        tx.objectStore(CONTENT_PARTITIONS_STORE).getAllKeys(),
        tx.objectStore(CONTENT_STAGED_CONFLICTS_STORE).getAllKeys(),
        tx.objectStore(CONTENT_CONFLICTS_STORE).getAllKeys(),
        tx.objectStore(CONTENT_OBSERVATIONS_STORE).getAllKeys(),
        tx.objectStore(KEEPSAKE_FENCES_STORE).getAllKeys(),
        tx.objectStore(KEEPSAKE_INTENTS_STORE).getAllKeys(),
        tx.objectStore(KEEPSAKE_META_STORE).getAllKeys(),
        tx.objectStore(KEEPSAKE_PINS_STORE).getAllKeys(),
        tx.objectStore(APP_META_STORE).getAllKeys(),
      ]);
    if (
      !sameStrings(sortedStringKeys(prayerKeys), expectedRowKeys(replacement.prayers)) ||
      audioKeys.length !== 0 ||
      !sameStrings(sortedStringKeys(bookmarkKeys), expectedRowKeys(replacement.bookmarks)) ||
      !sameStrings(sortedStringKeys(highlightKeys), expectedRowKeys(replacement.highlights)) ||
      !sameStrings(actualEntityKeys(entityKeys), expectedEntityKeys) ||
      holdingKeys.length !== 0 || pendingKeys.length !== 0 ||
      !sameStrings(sortedStringKeys(partitionKeys), [ownerId]) ||
      stagedKeys.length !== 0 || conflictKeys.length !== 0 || observationKeys.length !== 0 ||
      !sameStrings(sortedStringKeys(fenceKeys), expectedRowKeys(replacement.prayers)) ||
      intentKeys.length !== 0 ||
      !sameStrings(sortedStringKeys(keepsakeMetaKeys), [KEEPSAKE_EPOCH_KEY]) ||
      pinKeys.length !== 0 ||
      !sameStrings(
        sortedStringKeys(appMetaKeys),
        [CLOUD_ACCOUNT_META_KEY, CONTENT_OWNER_META_KEY, OWNER_ADOPTION_CLEANUP_META_KEY].sort(),
      )
    ) throw new Error('owner-workspace-install-postcondition-failed');
    assertSafe();
    await tx.done;
    if (options.abortSignal?.aborted) {
      throw new Error('owner-workspace-install-authorization-revoked');
    }
  } catch (error) {
    abort();
    await tx.done.catch(() => {});
    throw error;
  } finally {
    options.abortSignal?.removeEventListener('abort', abort);
  }
}

/**
 * Structural key prefix of the restored authoring-recovery rows (unexecuted
 * prayer attempts and testimony drafts) that the exact snapshot installer may
 * write into `app-meta`. Mirrors, kept identical by hand (rule #4):
 * `src/lib/prayer-command.ts` `PRAYER_ATTEMPT_PREFIX`, `scripts/ops/lib/lifecycle.mjs`
 * `PRAYER_ATTEMPT_PREFIX`, and the literal in `hasPositiveUnsyncedOwnerWorkInTx`
 * above, which is outside the installer and deliberately left as written. The
 * installer checks only this prefix and the owner it binds; the parsers in
 * `content-owner-restore.ts` are the value authority (db.ts stays parser-free).
 */
const PRAYER_ATTEMPT_KEY_PREFIX = 'prayer-authoring-attempt-v1:';

export interface ExactCoreContentReplacement {
  ownerId: string;
  /** Exact evidence counts derived by the closed snapshot verifier. */
  verifiedSourceEvidence: {
    deviceEscrowCount: number;
    ownedVolatileRowCount: number;
  };
  prayers: Prayer[];
  bookmarks: BibleBookmark[];
  highlights: BibleHighlight[];
  audioQueue: AudioQueueRow[];
  /**
   * Exact `app-meta` rows (attempt and draft records) already verified by the
   * snapshot verifier; written with `add` so any key collision aborts the
   * whole replacement.
   */
  authoringRecoveryRows: Array<{ key: string; value: string }>;
}

const EXACT_REPLACEMENT_STORES = [
  PRAYERS_STORE,
  AUDIO_QUEUE_STORE,
  BSB_BOOKS_STORE,
  BSB_META_STORE,
  BIBLE_BOOKMARKS_STORE,
  BIBLE_HIGHLIGHTS_STORE,
  BIBLE_PROGRESS_STORE,
  APP_META_STORE,
] as const;

function sortedStringKeys(keys: IDBValidKey[]): string[] {
  if (!keys.every((key) => typeof key === 'string')) {
    throw new Error('exact-replacement-non-string-key');
  }
  return (keys as string[]).slice().sort();
}

function expectedRowKeys(rows: Array<{ id: string }>): string[] {
  return rows.map((row) => row.id).slice().sort();
}

function assertExactKeys(label: string, actual: IDBValidKey[], expected: string[]): void {
  if (JSON.stringify(sortedStringKeys(actual)) !== JSON.stringify(expected)) {
    throw new Error(`exact-replacement-key-mismatch:${label}`);
  }
}

/**
 * Replace the complete owner-bound client corpus in ONE IndexedDB
 * transaction. This is intentionally narrower than the legacy restore bypass:
 * callers cannot run arbitrary writes. The transaction clears every
 * v8 user/account-bearing store, writes the verified snapshot (the four content
 * stores plus the carried authoring-recovery rows in app-meta), proves the
 * exact post-write key sets (including the empty progress store and an app-meta
 * store holding exactly the carried recovery keys), and only then records the
 * owner binding and cloud-account sentinel. Any exception aborts the clear, the
 * rows, and the binding together.
 */
export async function replaceCoreContentSnapshotAtomically(
  replacement: ExactCoreContentReplacement,
  options: { abortSignal?: AbortSignal } = {},
): Promise<void> {
  const abortSignal = options.abortSignal;
  const assertAuthorized = (): void => {
    if (abortSignal?.aborted) {
      throw new Error('exact-replacement-authorization-revoked');
    }
  };
  assertAuthorized();
  const ownerId = replacement.ownerId.trim();
  if (ownerId === '') throw new Error('exact-replacement-owner-required');
  const sourceEvidence = replacement.verifiedSourceEvidence;
  if (
    sourceEvidence === null || typeof sourceEvidence !== 'object' ||
    !Number.isInteger(sourceEvidence.deviceEscrowCount) ||
    sourceEvidence.deviceEscrowCount < 0 ||
    !Number.isInteger(sourceEvidence.ownedVolatileRowCount) ||
    sourceEvidence.ownedVolatileRowCount < 0 ||
    sourceEvidence.deviceEscrowCount + sourceEvidence.ownedVolatileRowCount < 1
  ) {
    throw new Error('exact-replacement-verified-source-evidence-required');
  }
  // Structural check only (the verifier parsed the values): every carried row
  // is a string key under this owner's attempt prefix with a string value, and
  // keys are unique. The binding keys never share the prefix, so the carried
  // set and the binding pair cannot collide.
  const recoveryRows = replacement.authoringRecoveryRows;
  const recoveryKeyPrefix = `${PRAYER_ATTEMPT_KEY_PREFIX}${ownerId}:`;
  if (
    !Array.isArray(recoveryRows) ||
    recoveryRows.some(
      (row) =>
        row === null || typeof row !== 'object' ||
        typeof row.key !== 'string' || !row.key.startsWith(recoveryKeyPrefix) ||
        typeof row.value !== 'string',
    ) ||
    new Set(recoveryRows.map((row) => row.key)).size !== recoveryRows.length
  ) {
    throw new Error('exact-replacement-recovery-row-invalid');
  }
  const recoveryKeys = recoveryRows.map((row) => row.key).slice().sort();

  const db = await getDb();
  if (db.version !== CORE_CUTOVER_DB_VERSION) {
    throw new Error('exact-replacement-requires-pristine-v8-partition');
  }
  const knownStores = new Set([
    PRAYERS_STORE,
    AUDIO_QUEUE_STORE,
    BSB_BOOKS_STORE,
    BSB_META_STORE,
    BIBLE_PROGRESS_STORE,
    BIBLE_BOOKMARKS_STORE,
    BIBLE_HIGHLIGHTS_STORE,
    APP_META_STORE,
  ]);
  for (const name of Array.from(db.objectStoreNames)) {
    if (!knownStores.has(name)) throw new Error('exact-replacement-unknown-store');
  }

  const tx = db.transaction(EXACT_REPLACEMENT_STORES, 'readwrite');
  const abortForRevokedAuthorization = (): void => {
    try {
      tx.abort();
    } catch {
      // It may already have aborted or committed. Synchronous checkpoints
      // below still prevent a revoked authorization from being accepted.
    }
  };
  abortSignal?.addEventListener('abort', abortForRevokedAuthorization, { once: true });
  try {
    assertAuthorized();
    const track = <T>(promise: Promise<T>): Promise<T> => {
      // Attach a rejection handler immediately. A later synchronous
      // DataCloneError while enqueueing another request aborts the transaction
      // and rejects already-enqueued requests; none may become an unhandled
      // rejection while the outer catch rolls the transaction back.
      void promise.catch(() => {});
      return promise;
    };
    const existingKeySets = await Promise.all(
      EXACT_REPLACEMENT_STORES.map((name) => track(tx.objectStore(name).getAllKeys())),
    );
    if (existingKeySets.some((keys) => keys.length > 0)) {
      throw new Error('exact-replacement-requires-pristine-empty-partition');
    }
    assertAuthorized();

    await Promise.all(
      EXACT_REPLACEMENT_STORES.map((name) => track(tx.objectStore(name).clear())),
    );

    await Promise.all([
      ...replacement.prayers.map((row) => track(tx.objectStore(PRAYERS_STORE).put(row))),
      ...replacement.audioQueue.map((row) => track(tx.objectStore(AUDIO_QUEUE_STORE).put(row))),
      ...replacement.bookmarks.map((row) => track(tx.objectStore(BIBLE_BOOKMARKS_STORE).put(row))),
      ...replacement.highlights.map((row) => track(tx.objectStore(BIBLE_HIGHLIGHTS_STORE).put(row))),
    ]);
    // `add`, never `put`: a collision with any existing app-meta row aborts the
    // whole replacement instead of silently replacing a row.
    await Promise.all(
      recoveryRows.map((row) =>
        track(tx.objectStore(APP_META_STORE).add({ key: row.key, value: row.value }))),
    );
    assertAuthorized();

    const [prayerKeys, audioKeys, bookCacheKeys, bibleMetaKeys, bookmarkKeys,
      highlightKeys, progressKeys, appMetaKeys] = await Promise.all([
      tx.objectStore(PRAYERS_STORE).getAllKeys(),
      tx.objectStore(AUDIO_QUEUE_STORE).getAllKeys(),
      tx.objectStore(BSB_BOOKS_STORE).getAllKeys(),
      tx.objectStore(BSB_META_STORE).getAllKeys(),
      tx.objectStore(BIBLE_BOOKMARKS_STORE).getAllKeys(),
      tx.objectStore(BIBLE_HIGHLIGHTS_STORE).getAllKeys(),
      tx.objectStore(BIBLE_PROGRESS_STORE).getAllKeys(),
      tx.objectStore(APP_META_STORE).getAllKeys(),
    ]);
    assertExactKeys('prayers', prayerKeys, expectedRowKeys(replacement.prayers));
    assertExactKeys('audio-queue', audioKeys, expectedRowKeys(replacement.audioQueue));
    assertExactKeys('bsb-books', bookCacheKeys, []);
    assertExactKeys('bsb-meta', bibleMetaKeys, []);
    assertExactKeys('bible-bookmarks', bookmarkKeys, expectedRowKeys(replacement.bookmarks));
    assertExactKeys('bible-highlights', highlightKeys, expectedRowKeys(replacement.highlights));
    assertExactKeys('bible-progress', progressKeys, []);
    assertExactKeys('app-meta-before-binding', appMetaKeys, recoveryKeys);
    assertAuthorized();

    // Binding is deliberately last. The cloud-account sentinel is part of the
    // same transaction and prevents a later writes-enabled build from treating
    // this restored cloud corpus as anonymous seed content.
    await Promise.all([
      tx.objectStore(APP_META_STORE).put({ key: CONTENT_OWNER_META_KEY, value: ownerId }),
      tx.objectStore(APP_META_STORE).put({ key: CLOUD_ACCOUNT_META_KEY, value: true }),
    ]);
    const finalMetaKeys = await tx.objectStore(APP_META_STORE).getAllKeys();
    assertExactKeys(
      'app-meta-after-binding',
      finalMetaKeys,
      [...recoveryKeys, CLOUD_ACCOUNT_META_KEY, CONTENT_OWNER_META_KEY].sort(),
    );
    assertAuthorized();
    await tx.done;
    assertAuthorized();
  } catch (error) {
    try {
      tx.abort();
    } catch {
      // The transaction may already have auto-aborted on a failed request.
    }
    void tx.done.catch(() => {});
    if (abortSignal?.aborted) {
      throw new Error('exact-replacement-authorization-revoked');
    }
    throw error;
  } finally {
    abortSignal?.removeEventListener('abort', abortForRevokedAuthorization);
  }
}

// ---- S0 keepsake protection accessors (PC-B rev 9, sections 10 + 10b.2) ----

/** Reread the DURABLE local prayer epoch (never a cached value). */
export async function getKeepsakeEpoch(): Promise<string> {
  const db = await getDb();
  const row = await db.get(KEEPSAKE_META_STORE, KEEPSAKE_EPOCH_KEY);
  return row && 'value' in row && typeof row.value === 'string' ? row.value : '0';
}

/** Advance the epoch alone (keepsake-policy intents advance it too). */
export async function bumpKeepsakeEpoch(): Promise<string> {
  assertDbWrite('db:keepsake-meta:epoch');
  const db = await getDb();
  const tx = db.transaction(KEEPSAKE_META_STORE, 'readwrite');
  const row = await tx.store.get(KEEPSAKE_EPOCH_KEY);
  const next = decimalIncrement(
    row && 'value' in row && typeof row.value === 'string' ? row.value : '0',
  );
  await tx.store.put({ key: 'local-prayer-epoch', value: next });
  await tx.done;
  epochBroadcast?.(next);
  return next;
}

export async function getKeepsakeFence(
  prayerId: string,
): Promise<LocalPrayerMutationFenceV1 | undefined> {
  const db = await getDb();
  return db.get(KEEPSAKE_FENCES_STORE, prayerId);
}

export async function getAllKeepsakeFences(): Promise<LocalPrayerMutationFenceV1[]> {
  const db = await getDb();
  return db.getAll(KEEPSAKE_FENCES_STORE);
}

/**
 * Conditional fence digest patch: applies only while the fence's
 * localMutationSeq still equals `seq` (a later mutation recomputes for
 * its own sequence). Used by the async post-write digest finalizer.
 */
export async function patchKeepsakeFenceDigest(
  prayerId: string,
  seq: string,
  payloadDigest: string,
): Promise<boolean> {
  assertDbWrite('db:keepsake-fence:digest');
  const db = await getDb();
  const tx = db.transaction(KEEPSAKE_FENCES_STORE, 'readwrite');
  const fence = await tx.store.get(prayerId);
  if (!fence || fence.localMutationSeq !== seq) {
    await tx.done;
    return false;
  }
  await tx.store.put({ ...fence, payloadDigest });
  await tx.done;
  return true;
}

/**
 * Conditional acknowledgement: a push response may acknowledge ONLY the
 * exact captured sequence while the digest still matches and the auth
 * epoch is unchanged. An older response can never acknowledge newer
 * bytes (the seq comparison closes the same-millisecond case).
 */
export async function acknowledgeKeepsakeFence(
  prayerId: string,
  seq: string,
  expectedDigest: string,
  expectedAuthEpoch: number,
): Promise<boolean> {
  assertDbWrite('db:keepsake-fence:ack');
  const db = await getDb();
  const tx = db.transaction(KEEPSAKE_FENCES_STORE, 'readwrite');
  const fence = await tx.store.get(prayerId);
  if (
    !fence ||
    fence.localMutationSeq !== seq ||
    fence.payloadDigest !== expectedDigest ||
    fence.authEpochAtMutation !== expectedAuthEpoch
  ) {
    await tx.done;
    return false;
  }
  await tx.store.put({
    ...fence,
    acknowledgedMutationSeq: seq,
    inFlightMutationSeq: null,
  });
  await tx.done;
  return true;
}

/** R4 reconciliation install: set ownerScope + equalize sequences. */
export async function resolveKeepsakeFence(
  prayerId: string,
  ownerScope: string,
  payloadDigest: string,
): Promise<boolean> {
  assertDbWrite('db:keepsake-fence:resolve');
  const db = await getDb();
  const tx = db.transaction(KEEPSAKE_FENCES_STORE, 'readwrite');
  const fence = await tx.store.get(prayerId);
  if (!fence) {
    await tx.done;
    return false;
  }
  await tx.store.put({
    ...fence,
    ownerScope,
    acknowledgedMutationSeq: fence.localMutationSeq,
    inFlightMutationSeq: null,
    payloadDigest,
  });
  await tx.done;
  return true;
}

export async function putKeepsakeIntent(
  intent: LocalKeepsakeValidityIntentV1,
): Promise<void> {
  assertDbWrite('db:keepsake-intent:put');
  const db = await getDb();
  await db.put(KEEPSAKE_INTENTS_STORE, intent);
}

export async function getKeepsakeIntent(
  mutationId: string,
): Promise<LocalKeepsakeValidityIntentV1 | undefined> {
  const db = await getDb();
  return db.get(KEEPSAKE_INTENTS_STORE, mutationId);
}

export async function getAllKeepsakeIntents(): Promise<LocalKeepsakeValidityIntentV1[]> {
  const db = await getDb();
  return db.getAll(KEEPSAKE_INTENTS_STORE);
}

export async function deleteKeepsakeIntent(mutationId: string): Promise<void> {
  assertDbWrite('db:keepsake-intent:delete');
  const db = await getDb();
  await db.delete(KEEPSAKE_INTENTS_STORE, mutationId);
}

/**
 * The mandatory gate snapshot: durable epoch + EVERY mutation fence +
 * the corpus fence + the complete validity journal read inside ONE IDB
 * readonly transaction (PC-B rev 9 section 10), so a suspended tab that
 * missed every broadcast still observes one consistent durable state.
 */
export async function readKeepsakeGateSnapshot(): Promise<{
  epoch: string;
  fences: LocalPrayerMutationFenceV1[];
  corpusFence: LocalPrayerCorpusFenceV1 | null;
  intents: LocalKeepsakeValidityIntentV1[];
}> {
  const db = await getDb();
  const tx = db.transaction(
    [KEEPSAKE_META_STORE, KEEPSAKE_FENCES_STORE, KEEPSAKE_INTENTS_STORE],
    'readonly',
  );
  const meta = tx.objectStore(KEEPSAKE_META_STORE);
  const [epochRow, corpusRow, fences, intents] = await Promise.all([
    meta.get(KEEPSAKE_EPOCH_KEY),
    meta.get(KEEPSAKE_CORPUS_FENCE_KEY),
    tx.objectStore(KEEPSAKE_FENCES_STORE).getAll(),
    tx.objectStore(KEEPSAKE_INTENTS_STORE).getAll(),
  ]);
  await tx.done;
  return {
    epoch:
      epochRow && 'value' in epochRow && typeof epochRow.value === 'string'
        ? epochRow.value
        : '0',
    fences,
    corpusFence: corpusRow && 'fence' in corpusRow ? corpusRow.fence : null,
    intents,
  };
}

export async function getKeepsakeCorpusFence(): Promise<LocalPrayerCorpusFenceV1 | null> {
  const db = await getDb();
  const row = await db.get(KEEPSAKE_META_STORE, KEEPSAKE_CORPUS_FENCE_KEY);
  return row && 'fence' in row ? row.fence : null;
}

export async function setKeepsakeCorpusFence(
  fence: LocalPrayerCorpusFenceV1,
): Promise<void> {
  assertDbWrite('db:keepsake-meta:corpus-fence');
  const db = await getDb();
  await db.put(KEEPSAKE_META_STORE, { key: 'corpus-fence', fence });
}

export async function getStoredPolicyPin(key: string): Promise<StoredPolicyPin | undefined> {
  const db = await getDb();
  return db.get(KEEPSAKE_PINS_STORE, key);
}

export async function putStoredPolicyPin(pin: StoredPolicyPin): Promise<void> {
  assertDbWrite('db:keepsake-pin:put');
  const db = await getDb();
  await db.put(KEEPSAKE_PINS_STORE, pin);
}

/**
 * Apply a POLICY-DRIVEN local projection change (privacy flag or a
 * restore re-add) in 'pulled' fence mode: the server row was already
 * changed by the owner-checked RPC, so the local write IS server state —
 * both fence sequences advance together and `updatedAt`/`syncedAt` are
 * preserved so the outbox does not re-push (a re-push would be a direct
 * write that invalidates the attestation the RPC just preserved).
 */
export async function applyPolicyProjection(prayer: Prayer): Promise<void> {
  assertDbWrite('db:prayer:policy-projection');
  const db = await getDb();
  const tx = db.transaction(
    [PRAYERS_STORE, KEEPSAKE_FENCES_STORE, KEEPSAKE_META_STORE],
    'readwrite',
  );
  void tx.objectStore(PRAYERS_STORE).put(prayer);
  const marks = await bumpFenceAndEpochInTx(tx as unknown as KeepsakeWriteTx, prayer.id, 'pulled');
  await tx.done;
  afterFenceWrite(prayer.id, marks.seq, marks.epoch);
}

// ---- Audio queue (B.2) ----
//
// Thin IDB wrappers; higher-level orchestration (single-flight,
// retry budget, transcribe routing) lives in `audio-queue.ts`. Keeping
// the persistence layer here means callers can mock IDB for tests
// without pulling in the network-touching processor.

// ---- Capture rows (account-change recording plan rev 4.1, section 4) --------
// `commitCaptureRow` is the only writer that creates a row carrying capture
// fields. Every later write that replaces an existing row runs
// `captureRowTransitionRefusal` inside its own transaction before any write is
// queued (or aborts the whole transaction when a row is read after other
// writes were queued). Frozen capture identity never changes; destination
// binding and processing state may.

// Mirrors UPLOAD_PROVED_REGISTRY_STATES in audio-queue.ts. It stays local here
// because db.ts is the persistence leaf and importing the queue orchestrator
// would create a cycle. Keep the two sets identical.
const CAPTURE_UPLOAD_PROVED_REGISTRY_STATES = new Set([
  'uploaded',
  'transcribing',
  'transcribed',
  'retry',
  'failed',
  'parked',
]);
const CAPTURE_RECOVERY_KINDS = new Set(['new-prayer', 'progress-update', 'testimony', 'bookmark']);
const SHA256_HEX = /^[0-9a-f]{64}$/;

/** Fixed at the first commit; compared when a retry verifies an existing row. */
const FROZEN_CAPTURE_FIELDS = [
  'id',
  'ownerId',
  'audioProtocolVersion',
  'authEpochAtCapture',
  'captureMode',
  'destinationId',
  'recoveryKind',
  'inlineSaveSource',
  'scriptureAnchor',
  'mimeType',
  'language',
  'capturedAt',
  'sizeBytes',
  'captureEvidence',
  'captureIdentity',
] as const satisfies readonly (keyof AudioQueueRow)[];

/** Mutable binding and processing state a first commit must not carry yet. */
const CAPTURE_UNBOUND_FIELDS = [
  'localDisposition',
  'quarantineReason',
  'firstPersistence',
  'registryRevision',
  'registryState',
  'destinationState',
  'destinationEntityType',
  'destinationEntityId',
  'destinationSubId',
  'destinationOpId',
  'recoveryLeaseToken',
  'recoveryLeaseExpiresAt',
  'storagePath',
  'byteSha256',
  'adoptedAt',
  'adoptedAuthEpoch',
] as const satisfies readonly (keyof AudioQueueRow)[];

// Stored rows are untrusted: IndexedDB's structured clone can hold BigInt,
// cycles, Dates, Maps, binary data and other non-JSON values. Everything below
// is total and fails closed: malformed capture data is refused, never thrown on.
const MALFORMED_CAPTURE_VALUE = Symbol('malformed-capture-value');
const CAPTURE_CANONICAL_MAX_DEPTH = 16;

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

/**
 * Key-order independent canonical form; `undefined` and `null` compare equal
 * (absent). Returns MALFORMED_CAPTURE_VALUE instead of throwing for a BigInt,
 * a non-finite number, a symbol or function, a cycle, excessive depth, an array
 * hole, or any object that is not a plain record or array.
 */
function canonicalCaptureValue(
  value: unknown,
  ancestors: readonly object[] = [],
): string | typeof MALFORMED_CAPTURE_VALUE {
  if (value === undefined || value === null) return 'null';
  switch (typeof value) {
    case 'string':
      return JSON.stringify(value);
    case 'boolean':
      return value ? 'true' : 'false';
    case 'number':
      return Number.isFinite(value) ? JSON.stringify(value) : MALFORMED_CAPTURE_VALUE;
    case 'object':
      break;
    default:
      return MALFORMED_CAPTURE_VALUE;
  }
  const object = value as object;
  if (ancestors.includes(object) || ancestors.length >= CAPTURE_CANONICAL_MAX_DEPTH) {
    return MALFORMED_CAPTURE_VALUE;
  }
  const nested = [...ancestors, object];
  if (Array.isArray(object)) {
    const parts: string[] = [];
    for (let index = 0; index < object.length; index += 1) {
      if (!(index in object)) return MALFORMED_CAPTURE_VALUE;
      const part = canonicalCaptureValue(object[index], nested);
      if (part === MALFORMED_CAPTURE_VALUE) return MALFORMED_CAPTURE_VALUE;
      parts.push(part);
    }
    return `[${parts.join(',')}]`;
  }
  if (!isPlainRecord(object)) return MALFORMED_CAPTURE_VALUE;
  const parts: string[] = [];
  for (const key of Object.keys(object).sort()) {
    if (object[key] === undefined) continue;
    const part = canonicalCaptureValue(object[key], nested);
    if (part === MALFORMED_CAPTURE_VALUE) return MALFORMED_CAPTURE_VALUE;
    parts.push(`${JSON.stringify(key)}:${part}`);
  }
  return `{${parts.join(',')}}`;
}

function hasExactKeys(record: Record<string, unknown>, keys: readonly string[]): boolean {
  const own = Object.keys(record);
  return own.length === keys.length && keys.every((key) => Object.prototype.hasOwnProperty.call(record, key));
}

const isSafeEpoch = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;
const CAPTURE_CLAIMANTS = new Set(['stop', 'interruption', 'teardown', 'drain', 'retry']);

function isCaptureEvidenceRecord(value: unknown): value is CaptureEvidenceV1 {
  return isPlainRecord(value) &&
    hasExactKeys(value, ['schema', 'kind', 'sha256', 'sizeBytes', 'mimeType']) &&
    value.schema === 'adia-capture-evidence-v1' &&
    (value.kind === 'blob' || value.kind === 'text') &&
    typeof value.sha256 === 'string' && SHA256_HEX.test(value.sha256) &&
    isSafeEpoch(value.sizeBytes) &&
    isNonEmptyString(value.mimeType);
}

function isCaptureIdentityRecord(value: unknown): value is CaptureIdentityV1 {
  return isPlainRecord(value) &&
    hasExactKeys(value, ['schema', 'custodyId', 'capturedOwnerId', 'capturedAuthEpoch', 'capturedPrayerId', 'sourceDeviceId']) &&
    value.schema === 'adia-capture-identity-v1' &&
    isNonEmptyString(value.custodyId) &&
    isNonEmptyString(value.capturedOwnerId) &&
    isSafeEpoch(value.capturedAuthEpoch) &&
    (value.capturedPrayerId === null || isNonEmptyString(value.capturedPrayerId)) &&
    isNonEmptyString(value.sourceDeviceId);
}

function isFirstPersistenceRecord(value: unknown): value is FirstPersistenceV1 {
  return isPlainRecord(value) &&
    hasExactKeys(value, ['schema', 'claimant', 'persistedAuthUserId', 'persistedAuthEpoch', 'networkBeforePersist']) &&
    value.schema === 'adia-first-persistence-v1' &&
    typeof value.claimant === 'string' && CAPTURE_CLAIMANTS.has(value.claimant) &&
    (value.persistedAuthUserId === null || isNonEmptyString(value.persistedAuthUserId)) &&
    isSafeEpoch(value.persistedAuthEpoch) &&
    value.networkBeforePersist === false;
}

/** Which stored capture record is malformed, or null when all three are valid. */
function malformedCaptureRecord(row: AudioQueueRow): string | null {
  if (!isCaptureEvidenceRecord(row.captureEvidence)) return 'captureEvidence';
  if (!isCaptureIdentityRecord(row.captureIdentity)) return 'captureIdentity';
  if (!isFirstPersistenceRecord(row.firstPersistence)) return 'firstPersistence';
  return null;
}

function isCaptureRow(row: AudioQueueRow): boolean {
  return row.captureEvidence !== undefined ||
    row.captureIdentity !== undefined ||
    row.firstPersistence !== undefined;
}

export class CaptureRowTransitionError extends Error {
  readonly reason: string;
  constructor(reason: string) {
    super(`capture-row-transition-refused:${reason}`);
    this.name = 'CaptureRowTransitionError';
    this.reason = reason;
  }
}

/** Compares one protected value on both sides; malformed on either side refuses. */
function protectedValueRefusal(field: string, left: unknown, right: unknown): string | null {
  const a = canonicalCaptureValue(left);
  const b = canonicalCaptureValue(right);
  if (a === MALFORMED_CAPTURE_VALUE || b === MALFORMED_CAPTURE_VALUE) return `malformed-${field}`;
  return a === b ? null : `frozen-${field}`;
}

function captureRowTransitionReason(current: AudioQueueRow, next: AudioQueueRow): string | null {
  if (!isPlainRecord(current) || !isPlainRecord(next)) return 'malformed-row';
  if (!isCaptureRow(current)) {
    if (isCaptureRow(next)) return 'capture-fields-added';
    return legacyRecordingTransitionReason(current, next);
  }
  const malformed = malformedCaptureRecord(current);
  if (malformed) return `malformed-${malformed}`;
  // The proposed replacement's three records must meet the same exact schemas
  // before the comparison below (which treats undefined as absent): an extra
  // own property, even one valued `undefined`, would otherwise be stored and
  // make the row permanently malformed.
  const malformedNext = malformedCaptureRecord(next);
  if (malformedNext) return `malformed-next-${malformedNext}`;
  for (const field of FROZEN_CAPTURE_FIELDS) {
    const reason = protectedValueRefusal(field, current[field], next[field]);
    if (reason) return reason;
  }
  const firstPersistence = protectedValueRefusal('firstPersistence', current.firstPersistence, next.firstPersistence);
  if (firstPersistence) return firstPersistence === 'frozen-firstPersistence' ? 'first-persistence' : firstPersistence;
  const evidence = current.captureEvidence as CaptureEvidenceV1;
  if (next.sizeBytes !== evidence.sizeBytes || next.mimeType !== evidence.mimeType) {
    return 'evidence-size-or-type';
  }
  if (evidence.kind === 'text') {
    // The text is the payload: it never changes and never gains bytes.
    if (next.blob !== undefined || next.storagePath !== undefined) return 'text-payload-bytes';
    if (next.transcribedText !== current.transcribedText) return 'text-payload-changed';
    if (next.byteSha256 !== undefined) return 'text-byte-digest';
    return null;
  }
  // The registration digest is computed from the same Blob, so any other
  // value means the bytes were substituted.
  if (next.byteSha256 !== undefined && next.byteSha256 !== evidence.sha256) {
    return 'byte-digest-mismatch';
  }
  if (next.blob !== current.blob) {
    // The same Blob object (read in this transaction and spread forward), the
    // existing proved-upload removal, or a verified copy of the current Blob
    // (see "Verified capture copies" below); never added back, never replaced
    // by anything else.
    if (!provedUploadRemoval(current, next) && !verifiedCopyReplacement(current, next)) return 'blob-changed';
  }
  return null;
}

/** The Blob is released only after a proved upload: a canonical path and a proved registry state. */
function provedUploadRemoval(current: AudioQueueRow, next: AudioQueueRow): boolean {
  return current.blob !== undefined &&
    next.blob === undefined &&
    typeof next.storagePath === 'string' &&
    next.storagePath.length > 0 &&
    typeof next.registryState === 'string' &&
    CAPTURE_UPLOAD_PROVED_REGISTRY_STATES.has(next.registryState);
}

/**
 * The Blob rule for an enhanced protocol-1 row without capture fields (a
 * recording made before v0.2.0, or written by an older tab; legacy
 * compatibility contract, section 6). Its Blob is carried forward as the same
 * object, released after a proved upload, or replaced by a legacy copy minted
 * for it (see "Recordings made before v0.2.0" below); never added back and
 * never replaced by anything else. Every other row without capture fields is
 * unchanged (no Blob rule).
 */
function legacyRecordingTransitionReason(current: AudioQueueRow, next: AudioQueueRow): string | null {
  if (current.audioProtocolVersion !== 1 || current.captureMode !== 'enhanced') return null;
  if (next.blob === current.blob) return null;
  if (provedUploadRemoval(current, next) || legacyCopyReplacement(current, next)) return null;
  return 'blob-changed';
}

// ---- Verified capture copies (KR8, operator-authorized 2026-10-19) ------------
//
// The one exception to "never replaced" above. WebKit ties a Blob read from
// IndexedDB to the Blob object last stored for its record while that object is
// alive, and to the stored file otherwise. Putting the read Blob back deletes
// the old file when the transaction commits, while the Blob just stored stays
// alive and backed by it, so every later read of the record fails ("The object
// can not be found here.") until JavaScript collects that Blob, and a Blob read
// before the rewrite cannot be sent as an upload body. A write that keeps a
// recording's Blob may store instead a fresh in-memory copy of the same bytes,
// read and proved against the frozen capture evidence by
// `materializeVerifiedCaptureCopy` before its transaction opens (reading and
// hashing are not IndexedDB work). A stored copy is backed by memory, never by
// a file a later write deletes, so reading its record never depends on when
// JavaScript collects anything. The guard accepts a changed Blob object only
// when it is such a copy, minted for this row and owner from this capture's
// unchanged identity and evidence, with the evidence's SHA-256 and length and
// the current Blob's exact type, while the current row still holds its Blob of
// that size and type. A released Blob is never added back; every other change
// stays `blob-changed`.
//
// Coverage (A2, 2026-10-19): every write that carries a recording's Blob
// forward goes through `verifiedCaptureCopyBeforeWrite` (or proves its own copy
// first, as adoption and the upload do): the generic seam
// `updateAudioQueueRowIfPresent` (so every registry merge, discovery included,
// the attempt and terminal writes and the transcript writes), the terminal and
// re-arm transactions, the Save link, and the content-lifecycle claim,
// coalesced rebinding and receipt supersession. When the bytes cannot be read
// (the write's own read for its copy fails), the write is refused before its
// transaction opens (`refuseUnreadableRecordingWrite`, LC2): nothing is written
// and the caller reports the failure, whatever the browser would do with such a
// put (WebKit was measured both aborting it, A2, and accepting it, LC run 08).
// When the bytes read but cannot be proved (they do not match, or there is
// nothing to prove them against), no copy is stored and the write keeps the
// stored Blob as before. Never empty or other bytes.

interface VerifiedCaptureCopyRecord {
  readonly kind: 'capture';
  readonly rowId: string;
  readonly ownerId: string;
  readonly identity: string;
  readonly evidence: string;
  readonly sha256: string;
  readonly sizeBytes: number;
  readonly type: string;
}

/** A copy of a recording made before v0.2.0, verified against a registry record (see "Recordings made before v0.2.0"). */
interface LegacyRecordingCopyRecord {
  readonly kind: 'legacy';
  readonly rowId: string;
  readonly ownerId: string;
  readonly proof: LegacyRecordingProof;
  readonly sha256: string;
  readonly sizeBytes: number;
  readonly type: string;
}

/** Filled only by `materializeVerifiedCaptureCopy`; module-private, so nothing else can make a Blob the guard accepts. */
const verifiedCaptureCopies = new WeakMap<Blob, VerifiedCaptureCopyRecord | LegacyRecordingCopyRecord>();

/** A verified, byte-identical in-memory copy of one row's recording. */
export interface VerifiedCaptureCopy {
  readonly rowId: string;
  readonly blob: Blob;
  readonly sha256: string;
  readonly sizeBytes: number;
}

export type VerifiedCaptureCopyFailure =
  | 'owner'
  | 'malformed'
  | 'byte-digest'
  | 'size-or-type'
  | 'row-read-failed'
  | 'read-failed'
  | 'length-mismatch'
  | 'digest-mismatch'
  | 'proof-binding';

export type VerifiedCaptureCopyResult =
  | { status: 'verified'; copy: VerifiedCaptureCopy }
  | { status: 'not-applicable' }
  | { status: 'unverified'; reason: VerifiedCaptureCopyFailure };

/** A recording's bytes could not be read, or did not match its capture evidence; nothing was written. */
export class CaptureBytesUnverifiedError extends Error {
  readonly reason: VerifiedCaptureCopyFailure;
  constructor(reason: VerifiedCaptureCopyFailure) {
    super(`capture-bytes-unverified:${reason}`);
    this.name = 'CaptureBytesUnverifiedError';
    this.reason = reason;
  }
}

/**
 * Read one recording's bytes from its stored row and prove them against the
 * row's frozen capture evidence (exact length and SHA-256, and the
 * registration digest when one is recorded), then build a new in-memory Blob
 * of the identical type. Runs before any transaction opens and writes nothing.
 * A recording made before v0.2.0 (no capture fields) is proved instead against
 * the registry proof kept for it in this page session, if any (see "Recordings
 * made before v0.2.0" below).
 * `not-applicable`: no row, or not a Blob capture that still holds its Blob (a
 * text capture, a recording made before v0.2.0 with no kept proof, a Blob
 * released after a proved upload); the caller writes exactly as before.
 * `unverified`: the bytes could not be read (`read-failed`) or do not match; no
 * copy exists, and empty or other bytes are never substituted. A failure to
 * read the row itself is `row-read-failed`, a storage failure rather than this
 * recording's byte read.
 */
export async function materializeVerifiedCaptureCopy(
  rowId: string,
  expectedOwnerId: string,
): Promise<VerifiedCaptureCopyResult> {
  const unverified = (reason: VerifiedCaptureCopyFailure): VerifiedCaptureCopyResult =>
    ({ status: 'unverified', reason });
  let row: AudioQueueRow | undefined;
  try {
    row = await getAudioQueueRow(rowId);
  } catch {
    // Reading the row is a storage failure, not this recording's byte read.
    return unverified('row-read-failed');
  }
  let stored: Blob;
  let evidence: CaptureEvidenceV1;
  let identity: string | typeof MALFORMED_CAPTURE_VALUE;
  let evidenceForm: string | typeof MALFORMED_CAPTURE_VALUE;
  try {
    if (!row || !isPlainRecord(row) || !(row.blob instanceof Blob)) return { status: 'not-applicable' };
    if (!isCaptureRow(row)) {
      // A recording made before v0.2.0: a copy only against a proof kept in
      // this page session (see "Recordings made before v0.2.0" below).
      const proof = isLegacyRecordingRow(row) ? keptLegacyRecordingProof(expectedOwnerId, rowId) : null;
      return proof ? materializeLegacyRecordingCopy(row, rowId, expectedOwnerId, proof) : { status: 'not-applicable' };
    }
    if (row.id !== rowId || row.ownerId !== expectedOwnerId) return unverified('owner');
    if (malformedCaptureRecord(row) !== null) return unverified('malformed');
    evidence = row.captureEvidence as CaptureEvidenceV1;
    identity = canonicalCaptureValue(row.captureIdentity);
    evidenceForm = canonicalCaptureValue(evidence);
    if (
      evidence.kind !== 'blob' ||
      identity === MALFORMED_CAPTURE_VALUE ||
      evidenceForm === MALFORMED_CAPTURE_VALUE
    ) return unverified('malformed');
    if (row.byteSha256 !== undefined && row.byteSha256 !== evidence.sha256) return unverified('byte-digest');
    stored = row.blob;
    if (stored.size !== evidence.sizeBytes || row.sizeBytes !== evidence.sizeBytes) {
      return unverified('size-or-type');
    }
  } catch {
    return unverified('malformed');
  }
  const type = stored.type;
  let bytes: ArrayBuffer;
  try {
    bytes = await stored.arrayBuffer();
  } catch {
    return unverified('read-failed');
  }
  if (bytes.byteLength !== evidence.sizeBytes) return unverified('length-mismatch');
  let digest: string;
  try {
    digest = await sha256Hex(bytes);
  } catch {
    return unverified('read-failed');
  }
  if (digest !== evidence.sha256) return unverified('digest-mismatch');
  const blob = new Blob([bytes], { type });
  if (blob.size !== evidence.sizeBytes || blob.type !== type) return unverified('size-or-type');
  verifiedCaptureCopies.set(blob, Object.freeze({
    kind: 'capture' as const,
    rowId,
    ownerId: expectedOwnerId,
    identity,
    evidence: evidenceForm,
    sha256: digest,
    sizeBytes: blob.size,
    type,
  }));
  return { status: 'verified', copy: Object.freeze({ rowId, blob, sha256: digest, sizeBytes: blob.size }) };
}

/**
 * A write that would carry a recording's Blob forward is refused before its
 * transaction opens when its own read of those bytes (the copy materialization
 * it does) failed: throws `CaptureBytesUnverifiedError('read-failed')`, and the
 * caller writes nothing (operator decision, LC2, 2026-10-19: preserve the
 * original record and bytes). Deterministic: it never depends on what the
 * browser does with a put of a Blob that cannot be read. Readable bytes that do
 * not match, a row with nothing to prove its bytes against (no byte read), and
 * a failure to read the row itself are not refused here.
 */
export function refuseUnreadableRecordingWrite(result: VerifiedCaptureCopyResult): void {
  if (result.status === 'unverified' && result.reason === 'read-failed') {
    throw new CaptureBytesUnverifiedError('read-failed');
  }
}

/**
 * The bytes were read in full but their length or SHA-256 does not match the
 * reference recorded when the recording was first saved (its capture evidence,
 * or the registry record kept as its proof). Such bytes are never sent (LC4).
 */
export function isRecordingBytesMismatch(reason: VerifiedCaptureCopyFailure): boolean {
  return reason === 'digest-mismatch' || reason === 'length-mismatch' || reason === 'size-or-type';
}

/**
 * `refuseMismatch` (LC4, the launch discovery only): an automatic write must
 * not rewrite a recording whose bytes do not match their reference (in WebKit
 * that rewrite leaves them unreadable in the page, and the person's own
 * recovery could then not be saved there). The write is refused before its
 * transaction with `CaptureBytesUnverifiedError(reason)`; nothing is written.
 */
export interface RecordingWriteOptions {
  refuseMismatch?: boolean;
}

/**
 * The verified copy a write that may carry `rowId`'s recording forward stores
 * (KR8): `provided` when the caller already decided (a copy, or `null` for none,
 * as release writes do), otherwise one proved now, before the caller's
 * transaction opens. When that proof's own read of the bytes fails, the write
 * is refused here (`refuseUnreadableRecordingWrite`). Null when the row holds no
 * Blob recording that can be proved, or its readable bytes do not match; the
 * write then keeps the stored Blob exactly as before and never substitutes
 * bytes.
 */
export async function verifiedCaptureCopyBeforeWrite(
  rowId: string,
  expectedOwnerId: string,
  provided?: VerifiedCaptureCopy | null,
  options?: RecordingWriteOptions,
): Promise<VerifiedCaptureCopy | null> {
  if (provided !== undefined) return provided;
  const result = await materializeVerifiedCaptureCopy(rowId, expectedOwnerId);
  refuseUnreadableRecordingWrite(result);
  if (options?.refuseMismatch && result.status === 'unverified' && isRecordingBytesMismatch(result.reason)) {
    throw new CaptureBytesUnverifiedError(result.reason);
  }
  return result.status === 'verified' ? result.copy : null;
}

/**
 * `verifiedCaptureCopyBeforeWrite` for several rows (each proved before the
 * caller's transaction); only proved copies are returned, and a row whose bytes
 * cannot be read refuses the whole write (`refuseUnreadableRecordingWrite`).
 */
export async function verifiedCaptureCopiesBeforeWrite(
  rowIds: readonly string[],
  expectedOwnerId: string,
): Promise<Map<string, VerifiedCaptureCopy>> {
  const copies = new Map<string, VerifiedCaptureCopy>();
  for (const rowId of new Set(rowIds)) {
    const copy = await verifiedCaptureCopyBeforeWrite(rowId, expectedOwnerId);
    if (copy) copies.set(rowId, copy);
  }
  return copies;
}

/**
 * Whether `next.blob` is a verified copy of `current`'s own present Blob: minted
 * for this row and owner from this capture's unchanged identity and evidence,
 * with the evidence's SHA-256 and length and the current Blob's exact size and
 * type. Total: anything unexpected is false.
 */
function verifiedCopyReplacement(current: AudioQueueRow, next: AudioQueueRow): boolean {
  try {
    const present = current.blob;
    const replacement = next.blob;
    if (!(present instanceof Blob) || !(replacement instanceof Blob)) return false;
    const record = verifiedCaptureCopies.get(replacement);
    if (!record || record.kind !== 'capture') return false;
    const evidence = current.captureEvidence as CaptureEvidenceV1;
    return evidence.kind === 'blob' &&
      record.rowId === current.id &&
      record.ownerId === current.ownerId &&
      record.identity === canonicalCaptureValue(current.captureIdentity) &&
      record.evidence === canonicalCaptureValue(evidence) &&
      record.sha256 === evidence.sha256 &&
      record.sizeBytes === evidence.sizeBytes &&
      replacement.size === record.sizeBytes &&
      replacement.type === record.type &&
      present.size === record.sizeBytes &&
      present.type === record.type &&
      (current.byteSha256 === undefined || current.byteSha256 === evidence.sha256);
  } catch {
    return false;
  }
}

/**
 * `next` with the verified copy stored in place of the Blob it carries forward
 * unchanged from `current`, when the guard accepts that copy for `current`;
 * otherwise `next` as given, so the write proceeds exactly as before (a released
 * or changed Blob, another row's copy, no copy). Synchronous and total: callers
 * run it inside their write transaction, after reading the current row there,
 * so it is decided at the write boundary.
 */
export function keepVerifiedCaptureCopy(
  current: AudioQueueRow,
  next: AudioQueueRow,
  copy: VerifiedCaptureCopy | null | undefined,
): AudioQueueRow {
  if (!copy || next.blob !== current.blob) return next;
  const candidate: AudioQueueRow = { ...next, blob: copy.blob };
  return verifiedCopyReplacement(current, candidate) || legacyCopyReplacement(current, candidate) ? candidate : next;
}

// ---- Recordings made before v0.2.0 (legacy compatibility, operator-authorized 2026-10-19) ----
//
// A recording captured by v0.1.4 or earlier, or written by an older tab, is an
// enhanced protocol-1 row with no capture fields: it has no frozen evidence to
// verify a copy against. When it was registered, the server registry record
// (migration 044) holds its byte length and SHA-256: v0.1.4 computed the digest
// from a complete read of the stored Blob before registering, the record never
// changes afterwards (a different replay quarantines it), and `uploaded` checks
// the stored object against it. That record is the reference; the row's own
// `byteSha256` and `sizeBytes` are never the reference, they must only agree.
//
// A proof is the record's {owner, queue id, storage path, byte size, SHA-256,
// MIME type}. audio-registry.ts keeps one in page memory only when the record
// existed before the operation that read the bytes (a discovery answer, or an
// answer for a row already registered when the operation began; never the
// first registration that computed the digest), is not quarantined, and binds
// to the local row with no conflict. A kept proof is cleared on any owner or
// auth-epoch change and is never persisted. Every write that keeps the Blob
// then mints, before its transaction, a fresh copy whose bytes were read in
// full and match the proof's length and SHA-256, and the guard accepts that
// copy only for the row and proof it was minted for (`legacyCopyReplacement`).
// When a kept proof's read of the bytes fails, the write is refused before its
// transaction (`refuseUnreadableRecordingWrite`). Without a kept proof
// (unregistered, missing, quarantined or conflicting record; no byte read is
// made), or when the readable bytes do not match, no copy is made and the write
// keeps the stored Blob exactly as before; nothing fabricates capture evidence
// or substitutes bytes.

/** The registry record's reference for one recording made before v0.2.0. */
export interface LegacyRecordingProof {
  readonly ownerId: string;
  readonly queueId: string;
  readonly storagePath: string;
  readonly byteSize: number;
  readonly byteSha256: string;
  readonly mimeType: string;
}

type LegacyRecordingProofLookup = (ownerId: string, queueId: string) => LegacyRecordingProof | null;
let legacyRecordingProofLookup: LegacyRecordingProofLookup | null = null;

/**
 * Installed by audio-registry.ts, which keeps the proofs (db.ts is the
 * persistence leaf and cannot import the auth epoch the proofs are bound to).
 */
export function setLegacyRecordingProofLookup(lookup: LegacyRecordingProofLookup): boolean {
  // Installed once (LC5): a later call is ignored, so it can never replace the
  // registry's lookup; a module evaluated again can only lose proofs.
  if (legacyRecordingProofLookup !== null) return false;
  legacyRecordingProofLookup = lookup;
  return true;
}

function keptLegacyRecordingProof(ownerId: string, queueId: string): LegacyRecordingProof | null {
  try {
    return legacyRecordingProofLookup?.(ownerId, queueId) ?? null;
  } catch {
    return null;
  }
}

/** An enhanced protocol-1 row with no capture fields (Blob presence is checked by the caller). */
function isLegacyRecordingRow(row: AudioQueueRow): boolean {
  return !isCaptureRow(row) && row.audioProtocolVersion === 1 && row.captureMode === 'enhanced';
}

const unsetOrEqual = (value: unknown, expected: unknown): boolean =>
  value === undefined || value === null || value === expected;

/** The row's own id, owner and MIME type equal the proof, and so do its path, size and digest where set. */
function legacyProofBinds(row: AudioQueueRow, proof: LegacyRecordingProof): boolean {
  return row.id === proof.queueId &&
    row.ownerId === proof.ownerId &&
    row.mimeType === proof.mimeType &&
    unsetOrEqual(row.storagePath, proof.storagePath) &&
    unsetOrEqual(row.sizeBytes, proof.byteSize) &&
    unsetOrEqual(row.byteSha256, proof.byteSha256);
}

/** Whether `proof` may serve `row`: a recording made before v0.2.0 that still holds its Blob and binds to the proof. Total. */
export function legacyRecordingProofApplies(row: AudioQueueRow | undefined, proof: LegacyRecordingProof): boolean {
  try {
    return Boolean(row) && isPlainRecord(row) && isLegacyRecordingRow(row as AudioQueueRow) &&
      (row as AudioQueueRow).blob instanceof Blob && legacyProofBinds(row as AudioQueueRow, proof);
  } catch {
    return false;
  }
}

/** Reads the stored Blob in full and proves it against `proof`; mints a copy only on an exact match. Writes nothing. */
async function materializeLegacyRecordingCopy(
  row: AudioQueueRow,
  rowId: string,
  expectedOwnerId: string,
  proof: LegacyRecordingProof,
): Promise<VerifiedCaptureCopyResult> {
  const unverified = (reason: VerifiedCaptureCopyFailure): VerifiedCaptureCopyResult =>
    ({ status: 'unverified', reason });
  if (row.id !== rowId || row.ownerId !== expectedOwnerId || proof.ownerId !== expectedOwnerId || proof.queueId !== rowId) {
    return unverified('owner');
  }
  if (!legacyRecordingProofApplies(row, proof)) return unverified('proof-binding');
  const stored = row.blob as Blob;
  if (stored.size !== proof.byteSize) return unverified('size-or-type');
  const type = stored.type;
  let bytes: ArrayBuffer;
  try {
    bytes = await stored.arrayBuffer();
  } catch {
    return unverified('read-failed');
  }
  if (bytes.byteLength !== proof.byteSize) return unverified('length-mismatch');
  let digest: string;
  try {
    digest = await sha256Hex(bytes);
  } catch {
    return unverified('read-failed');
  }
  if (digest !== proof.byteSha256) return unverified('digest-mismatch');
  const blob = new Blob([bytes], { type });
  if (blob.size !== proof.byteSize || blob.type !== type) return unverified('size-or-type');
  verifiedCaptureCopies.set(blob, Object.freeze({
    kind: 'legacy' as const,
    rowId,
    ownerId: expectedOwnerId,
    proof: Object.freeze({ ...proof }),
    sha256: digest,
    sizeBytes: blob.size,
    type,
  }));
  return { status: 'verified', copy: Object.freeze({ rowId, blob, sha256: digest, sizeBytes: blob.size }) };
}

/**
 * Whether `next.blob` is a legacy copy minted for `current`: the same row and
 * owner, `current` still a recording made before v0.2.0 holding a Blob of the
 * copy's exact size and type, and both `current` and `next` still bound to the
 * copy's proof (path, size and digest where set). Total: anything unexpected is
 * false.
 */
function legacyCopyReplacement(current: AudioQueueRow, next: AudioQueueRow): boolean {
  try {
    const present = current.blob;
    const replacement = next.blob;
    if (!(present instanceof Blob) || !(replacement instanceof Blob)) return false;
    const record = verifiedCaptureCopies.get(replacement);
    if (!record || record.kind !== 'legacy') return false;
    const proof = record.proof;
    return isLegacyRecordingRow(current) &&
      !isCaptureRow(next) &&
      record.rowId === current.id &&
      record.ownerId === current.ownerId &&
      next.id === current.id &&
      next.ownerId === current.ownerId &&
      legacyProofBinds(current, proof) &&
      legacyProofBinds(next, proof) &&
      record.sha256 === proof.byteSha256 &&
      record.sizeBytes === proof.byteSize &&
      replacement.size === record.sizeBytes &&
      replacement.type === record.type &&
      present.size === record.sizeBytes &&
      present.type === record.type;
  } catch {
    return false;
  }
}

/**
 * LC7 (review D-A): the queue ids that a stored pending Prayer names in
 * `pendingTranscriptionId` but that have no `audio-queue` row on this device.
 * One read-only transaction over the owner binding, the Prayers and the queue,
 * so a Save's link (Prayer and row in one transaction) is never seen half
 * done; only while the durable binding is `ownerId` (otherwise nothing). Reads
 * rows, never their bytes.
 */
export async function pendingPrayerRecordingsMissing(ownerId: string): Promise<string[]> {
  const db = await getDb();
  const tx = db.transaction([APP_META_STORE, PRAYERS_STORE, AUDIO_QUEUE_STORE], 'readonly');
  const [binding, prayers, queueIds] = await Promise.all([
    tx.objectStore(APP_META_STORE).get(CONTENT_OWNER_META_KEY),
    tx.objectStore(PRAYERS_STORE).getAll(),
    tx.objectStore(AUDIO_QUEUE_STORE).getAllKeys(),
  ]);
  await tx.done;
  if (binding?.value !== ownerId) return [];
  const present = new Set(queueIds.map(String));
  const missing = new Set<string>();
  for (const prayer of prayers) {
    const queueId = prayer.pendingTranscriptionId;
    if (typeof queueId === 'string' && queueId && !present.has(queueId)) missing.add(queueId);
  }
  return [...missing];
}

/**
 * Queue progress (legacy compatibility contract, section 10): whether the
 * stored Blob of `rowId` can be read in full right now. Only the byte read
 * decides: a row that is missing or holds no Blob is `true` (nothing to read),
 * and a failure to read the row itself is thrown like any other storage
 * failure. Writes nothing.
 */
export async function storedRecordingBytesReadable(rowId: string): Promise<boolean> {
  const row = await getAudioQueueRow(rowId);
  if (!row || !(row.blob instanceof Blob)) return true;
  try {
    await row.blob.arrayBuffer();
    return true;
  } catch {
    return false;
  }
}

/**
 * Pure, synchronous and total, so a caller can run it inside an IndexedDB
 * transaction between a read and a write. Returns the refusal instead of
 * throwing so the caller aborts its whole transaction before rethrowing; any
 * unexpected exception while inspecting stored data is itself a refusal.
 */
export function captureRowTransitionRefusal(
  current: AudioQueueRow,
  next: AudioQueueRow,
): CaptureRowTransitionError | null {
  let reason: string | null;
  try {
    reason = captureRowTransitionReason(current, next);
  } catch {
    reason = 'malformed-guard-error';
  }
  return reason === null ? null : new CaptureRowTransitionError(reason);
}

/** Throwing form, for callers outside a transaction (and probes). */
export function assertCaptureRowTransition(current: AudioQueueRow, next: AudioQueueRow): void {
  const refusal = captureRowTransitionRefusal(current, next);
  if (refusal) throw refusal;
}

type AbortableTransaction = { abort(): void; done: Promise<unknown> };

/**
 * Aborts the whole transaction (every queued write) and rethrows the refusal.
 * Tolerates a transaction IndexedDB already aborted, so the refusal is always
 * the error the caller sees. Used by the content-lifecycle guard sites too.
 */
export async function abortForCaptureRefusal(
  tx: AbortableTransaction,
  refusal: Error,
): Promise<never> {
  try {
    tx.abort();
  } catch {
    // Already finished or aborted; the refusal is still the outcome.
  }
  await tx.done.catch(() => {});
  throw refusal;
}

export type CaptureCommitErrorCode =
  | 'capture-input-invalid'
  | 'owner-binding-refused'
  | 'identity-conflict'
  | 'auth-unsettled';

export class CaptureCommitError extends Error {
  readonly code: CaptureCommitErrorCode;
  constructor(code: CaptureCommitErrorCode, detail?: string) {
    super(detail ? `${code}:${detail}` : code);
    this.name = 'CaptureCommitError';
    this.code = code;
  }
}

export interface CaptureCommitInput {
  /**
   * The capture as custody holds it: frozen fields, payload, `captureEvidence`
   * and `captureIdentity`, with no binding or processing state yet.
   * `firstPersistence`, `localDisposition` and `quarantineReason` are derived
   * inside the transaction.
   */
  row: AudioQueueRow;
  claimant: CaptureClaimant;
}

export interface CaptureAuthSnapshot {
  /** `undefined` while auth is still resolving; `null` when signed out. */
  userId: string | null | undefined;
  epoch: number;
}

export type CaptureCommitResult =
  | { status: 'created'; row: AudioQueueRow }
  | { status: 'verified'; row: AudioQueueRow };

/** The settled bound for the auth class (plan rev 4.1, 3.6). */
export const CAPTURE_COMMIT_AUTH_ATTEMPTS = 3;

async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** Validates the capture and proves its evidence against the payload. Runs
 * before the transaction opens: hashing is not IndexedDB work. */
async function assertCaptureCommitInput(row: AudioQueueRow, expectedOwnerId: string): Promise<void> {
  const invalid = (detail: string) => new CaptureCommitError('capture-input-invalid', detail);
  const identity = row.captureIdentity;
  const evidence = row.captureEvidence;
  if (!isCaptureIdentityRecord(identity)) throw invalid('identity');
  if (!isCaptureEvidenceRecord(evidence)) throw invalid('evidence');
  // Every frozen field must have a canonical form now, so the stored row can
  // always be compared later (a malformed stored capture is refused forever).
  for (const field of FROZEN_CAPTURE_FIELDS) {
    if (canonicalCaptureValue(row[field]) === MALFORMED_CAPTURE_VALUE) throw invalid(`malformed-${field}`);
  }
  if (typeof row.id !== 'string' || row.id.length === 0 || identity.custodyId !== row.id) throw invalid('custody-id');
  if (row.ownerId !== expectedOwnerId || identity.capturedOwnerId !== expectedOwnerId) throw invalid('owner');
  if (
    typeof row.authEpochAtCapture !== 'number' ||
    !Number.isSafeInteger(row.authEpochAtCapture) ||
    identity.capturedAuthEpoch !== row.authEpochAtCapture
  ) throw invalid('auth-epoch');
  if (identity.capturedPrayerId !== row.prayerId) throw invalid('prayer-id');
  if (
    typeof row.sourceDeviceId !== 'string' ||
    row.sourceDeviceId.length === 0 ||
    identity.sourceDeviceId !== row.sourceDeviceId
  ) throw invalid('source-device');
  if (row.audioProtocolVersion !== 1) throw invalid('protocol');
  if (typeof row.destinationId !== 'string' || row.destinationId.length === 0) throw invalid('destination');
  if (!row.recoveryKind || !CAPTURE_RECOVERY_KINDS.has(row.recoveryKind)) throw invalid('recovery-kind');
  if (typeof row.capturedAt !== 'string' || Number.isNaN(Date.parse(row.capturedAt))) throw invalid('captured-at');
  if (typeof row.language !== 'string' || row.language.length === 0) throw invalid('language');
  for (const field of CAPTURE_UNBOUND_FIELDS) {
    if (row[field] !== undefined) throw invalid(`unbound-${field}`);
  }
  if (row.failed !== false || row.attemptCount !== 0) throw invalid('processing-state');
  if (
    !SHA256_HEX.test(evidence.sha256) ||
    !Number.isSafeInteger(evidence.sizeBytes) ||
    evidence.sizeBytes < 0 ||
    evidence.sizeBytes !== row.sizeBytes ||
    evidence.mimeType !== row.mimeType
  ) throw invalid('evidence-fields');
  if (evidence.kind === 'blob') {
    if (row.captureMode !== 'enhanced') throw invalid('blob-capture-mode');
    if (!(row.blob instanceof Blob) || row.blob.size !== evidence.sizeBytes || evidence.sizeBytes === 0) {
      throw invalid('blob-payload');
    }
    if (row.transcribedText !== null) throw invalid('blob-transcript');
    if (await sha256Hex(await row.blob.arrayBuffer()) !== evidence.sha256) throw invalid('blob-digest');
    return;
  }
  if (evidence.kind !== 'text') throw invalid('evidence-kind');
  if (row.captureMode !== 'browser') throw invalid('text-capture-mode');
  if (row.blob !== undefined || row.mimeType !== 'text/plain' || evidence.sizeBytes !== 0) {
    throw invalid('text-payload');
  }
  if (typeof row.transcribedText !== 'string' || row.transcribedText.trim().length === 0) {
    throw invalid('text-empty');
  }
  const text = new TextEncoder().encode(row.transcribedText);
  if (await sha256Hex(text.buffer.slice(text.byteOffset, text.byteOffset + text.byteLength)) !== evidence.sha256) {
    throw invalid('text-digest');
  }
}

/** Verification compares the capture identity only, never `firstPersistence`,
 * so a retry under a different auth, user or claimant still verifies. */
function captureIdentityMatches(stored: AudioQueueRow, capture: AudioQueueRow): boolean {
  try {
    // A stored row that is not a well-formed capture never verifies (fail closed).
    if (!isPlainRecord(stored) || malformedCaptureRecord(stored) !== null) return false;
    for (const field of FROZEN_CAPTURE_FIELDS) {
      if (protectedValueRefusal(field, stored[field], capture[field]) !== null) return false;
    }
    return capture.captureEvidence?.kind !== 'text' || stored.transcribedText === capture.transcribedText;
  } catch {
    return false;
  }
}

async function commitCaptureRowOnce(
  input: CaptureCommitInput,
  expectedOwnerId: string,
  readAuth: () => CaptureAuthSnapshot,
): Promise<CaptureCommitResult> {
  assertDbWrite('db:audio-queue:commit-capture');
  const capture = input.row;
  const identity = capture.captureIdentity as CaptureIdentityV1;
  const db = await getDb();
  const tx = db.transaction([AUDIO_QUEUE_STORE, APP_META_STORE], 'readwrite');
  void tx.done.catch(() => {});
  // The binding read inside this transaction is authoritative.
  const binding = await tx.objectStore(APP_META_STORE).get(CONTENT_OWNER_META_KEY);
  if (binding?.value !== expectedOwnerId) {
    return abortForCaptureRefusal(tx, new CaptureCommitError('owner-binding-refused'));
  }
  const queue = tx.objectStore(AUDIO_QUEUE_STORE);
  const stored = await queue.get(capture.id);
  if (stored) {
    if (!captureIdentityMatches(stored, capture)) {
      return abortForCaptureRefusal(tx, new CaptureCommitError('identity-conflict'));
    }
    // Verified: nothing is written, so the first save's record stays exactly.
    await tx.done;
    return { status: 'verified', row: stored };
  }
  // After the row read and immediately before `add`: the only auth snapshot
  // that decides provenance and disposition. Earlier checks are advisory.
  const auth = readAuth();
  if (auth.userId === undefined) {
    return abortForCaptureRefusal(tx, new CaptureCommitError('auth-unsettled'));
  }
  const ownerCurrent =
    auth.userId === identity.capturedOwnerId && auth.epoch === identity.capturedAuthEpoch;
  const created: AudioQueueRow = {
    ...capture,
    localDisposition: ownerCurrent ? 'unresolved' : 'quarantined',
    ...(ownerCurrent ? {} : { quarantineReason: 'account-change-preservation' as const }),
    firstPersistence: {
      schema: 'adia-first-persistence-v1',
      claimant: input.claimant,
      persistedAuthUserId: auth.userId,
      persistedAuthEpoch: auth.epoch,
      networkBeforePersist: false,
    },
  };
  try {
    // `add`, never `put`: a concurrent writer surfaces as an error, never an
    // overwrite.
    await queue.add(created);
  } catch (error) {
    await tx.done.catch(() => {});
    throw error;
  }
  // Durable only when the transaction completes; an abort leaves nothing saved.
  await tx.done;
  return { status: 'created', row: created };
}

/**
 * Create the capture row if absent, or verify the exact existing capture
 * without writing (plan rev 4.1, 4.2). `readAuth` supplies the current auth
 * user and epoch (the caller passes `getAuthUserId` / `getAuthEpoch`; db.ts
 * stays a persistence leaf). Only the `auth-unsettled` class is retried here,
 * at most `CAPTURE_COMMIT_AUTH_ATTEMPTS` times per invocation; identity
 * conflicts and owner-binding refusals are never retried.
 */
export async function commitCaptureRow(
  input: CaptureCommitInput,
  expectedOwnerId: string,
  readAuth: () => CaptureAuthSnapshot,
): Promise<CaptureCommitResult> {
  assertDbWrite('db:audio-queue:commit-capture');
  await assertCaptureCommitInput(input.row, expectedOwnerId);
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await commitCaptureRowOnce(input, expectedOwnerId, readAuth);
    } catch (error) {
      const unsettled = error instanceof CaptureCommitError && error.code === 'auth-unsettled';
      if (!unsettled || attempt >= CAPTURE_COMMIT_AUTH_ATTEMPTS) throw error;
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }
}

// ---- Part R: explicit adoption of kept recordings ------------------------------
//
// Account-change recording plan rev 4.1, section 9, as revised by Part R
// proposal revision 2 and its amendment 1. Additive: nothing above changes.

/**
 * Exact, collision-free snapshot of a stored row (amendment A1). Every value at
 * every depth carries its own tag, so stored data can never collide with an
 * encoding: an own property valued `undefined` is ["u"] (an absent property
 * emits nothing), `null` ["n"], booleans ["b", v], text ["s", v], finite
 * numbers ["d", decimal] with -0 as ["d","-0"], dense arrays ["a", [...]],
 * plain objects ["o", [[key, node], ...]] with keys sorted, and only the row's
 * top-level `blob` may be a Blob, ["B", size, type] (its bytes are never read).
 * Total: anything else (a non-finite number, BigInt, symbol, function, array
 * hole, extra array property, own symbol key, accessor, non-enumerable
 * property, non-plain object, cycle, depth beyond 16) returns null. Kept apart
 * from `canonicalCaptureValue`, whose undefined/null folding is intentional for
 * capture verification.
 */
const STRICT_SNAPSHOT_MAX_DEPTH = 16;

class StrictSnapshotMalformed extends Error {}

function strictSnapshotNode(value: unknown, ancestors: readonly object[]): unknown[] {
  if (value === undefined) return ['u'];
  if (value === null) return ['n'];
  switch (typeof value) {
    case 'boolean':
      return ['b', value];
    case 'string':
      return ['s', value];
    case 'number':
      if (!Number.isFinite(value)) throw new StrictSnapshotMalformed();
      return ['d', Object.is(value, -0) ? '-0' : String(value)];
    case 'object':
      break;
    default:
      throw new StrictSnapshotMalformed();
  }
  const object = value as object;
  if (ancestors.includes(object) || ancestors.length >= STRICT_SNAPSHOT_MAX_DEPTH) {
    throw new StrictSnapshotMalformed();
  }
  if (Object.getOwnPropertySymbols(object).length > 0) throw new StrictSnapshotMalformed();
  const nested = [...ancestors, object];
  if (Array.isArray(object)) {
    if (Object.getPrototypeOf(object) !== Array.prototype) throw new StrictSnapshotMalformed();
    const names = Object.getOwnPropertyNames(object);
    // Exactly the indices 0..length-1 plus `length`: no holes, no extras.
    if (names.length !== object.length + 1) throw new StrictSnapshotMalformed();
    const items: unknown[] = [];
    for (let index = 0; index < object.length; index += 1) {
      const descriptor = Object.getOwnPropertyDescriptor(object, index);
      if (!descriptor || !('value' in descriptor) || !descriptor.enumerable) {
        throw new StrictSnapshotMalformed();
      }
      items.push(strictSnapshotNode(descriptor.value, nested));
    }
    return ['a', items];
  }
  return ['o', strictSnapshotEntries(object, nested, false)];
}

function strictSnapshotEntries(
  object: object,
  nested: readonly object[],
  topLevelRow: boolean,
): unknown[] {
  if (!isPlainRecord(object)) throw new StrictSnapshotMalformed();
  // At the root as well as nested: an own symbol key is never encodable.
  if (Object.getOwnPropertySymbols(object).length > 0) throw new StrictSnapshotMalformed();
  const entries: unknown[] = [];
  for (const name of Object.getOwnPropertyNames(object).sort()) {
    const descriptor = Object.getOwnPropertyDescriptor(object, name);
    if (!descriptor || !('value' in descriptor) || !descriptor.enumerable) {
      throw new StrictSnapshotMalformed();
    }
    const value: unknown = descriptor.value;
    if (topLevelRow && name === 'blob' && value !== undefined) {
      if (typeof Blob === 'undefined' || !(value instanceof Blob)) throw new StrictSnapshotMalformed();
      entries.push([name, ['B', value.size, value.type]]);
      continue;
    }
    entries.push([name, strictSnapshotNode(value, nested)]);
  }
  return entries;
}

export function strictRowSnapshot(row: unknown): string | null {
  try {
    if (!isPlainRecord(row)) return null;
    return JSON.stringify(['o', strictSnapshotEntries(row, [row], true)]);
  } catch {
    return null;
  }
}

/** Fields a kept recording must not carry: nothing bound, registered or adopted. */
const KEPT_CAPTURE_ABSENT_FIELDS = [
  'registryRevision',
  'registryState',
  'destinationState',
  'destinationEntityType',
  'destinationEntityId',
  'destinationSubId',
  'destinationOpId',
  'recoveryLeaseToken',
  'recoveryLeaseExpiresAt',
  'storagePath',
  'adoptedAt',
  'adoptedAuthEpoch',
] as const satisfies readonly (keyof AudioQueueRow)[];

/**
 * Why a stored row is not an adoptable kept recording for `ownerId`, or null
 * when it is (plan 9.1, proposal revision 2 section 2). Pure and total: any
 * malformed data or exception is a refusal, never a throw. Text evidence is
 * further checked against the verified text by the caller and in
 * `adoptKeptCapture`.
 */
export function keptCaptureRefusal(row: AudioQueueRow, ownerId: string): string | null {
  try {
    if (!isPlainRecord(row)) return 'malformed-row';
    if (strictRowSnapshot(row) === null) return 'malformed-snapshot';
    if (row.localDisposition !== 'quarantined') return 'not-quarantined';
    if (row.quarantineReason !== 'account-change-preservation') return 'quarantine-reason';
    const malformed = malformedCaptureRecord(row);
    if (malformed) return `malformed-${malformed}`;
    for (const field of FROZEN_CAPTURE_FIELDS) {
      if (canonicalCaptureValue(row[field]) === MALFORMED_CAPTURE_VALUE) return `malformed-${field}`;
    }
    const identity = row.captureIdentity as CaptureIdentityV1;
    const persistence = row.firstPersistence as FirstPersistenceV1;
    const evidence = row.captureEvidence as CaptureEvidenceV1;
    if (typeof row.id !== 'string' || identity.custodyId !== row.id) return 'custody-id';
    if (row.ownerId !== ownerId || identity.capturedOwnerId !== ownerId) return 'owner';
    if (row.authEpochAtCapture !== identity.capturedAuthEpoch) return 'auth-epoch';
    if (
      persistence.persistedAuthUserId === identity.capturedOwnerId &&
      persistence.persistedAuthEpoch === identity.capturedAuthEpoch
    ) return 'persisted-under-capture-identity';
    if (persistence.networkBeforePersist !== false) return 'network-before-persist';
    if (row.audioProtocolVersion !== 1) return 'protocol';
    if (!row.recoveryKind || !CAPTURE_RECOVERY_KINDS.has(row.recoveryKind)) return 'recovery-kind';
    // Checked, never repaired: listing sorts by it.
    if (typeof row.capturedAt !== 'string' || Number.isNaN(Date.parse(row.capturedAt))) {
      return 'captured-at';
    }
    const pairedMode =
      (row.captureMode === 'enhanced' && evidence.kind === 'blob') ||
      (row.captureMode === 'browser' && evidence.kind === 'text');
    if (!pairedMode) return 'mode-evidence-kind';
    for (const field of KEPT_CAPTURE_ABSENT_FIELDS) {
      if (row[field] !== undefined) return `bound-${field}`;
    }
    if (row.failed !== false || row.attemptCount !== 0) return 'processing-state';
    if (row.prayerId !== identity.capturedPrayerId) return 'prayer-binding';
    if (row.sizeBytes !== evidence.sizeBytes || row.mimeType !== evidence.mimeType) {
      return 'evidence-size-or-type';
    }
    if (evidence.kind === 'blob') {
      if (!(row.blob instanceof Blob) || row.blob.size !== evidence.sizeBytes) return 'blob-payload';
      // The stored Blob's own type must be the recorded one (no re-hash).
      if (row.blob.type !== evidence.mimeType || row.blob.type !== row.mimeType) return 'blob-type';
      if (row.byteSha256 !== undefined && row.byteSha256 !== evidence.sha256) return 'byte-digest';
      return null;
    }
    if (row.blob !== undefined) return 'text-blob';
    if (row.mimeType !== 'text/plain' || evidence.sizeBytes !== 0) return 'text-payload';
    if (row.byteSha256 !== undefined) return 'text-byte-digest';
    if (typeof row.transcribedText !== 'string' || row.transcribedText.trim().length === 0) {
      return 'text-empty';
    }
    return null;
  } catch {
    return 'malformed-guard-error';
  }
}

/**
 * True only when `current` is the same capture as the row this page listed and
 * was adopted after that listing (proposal revision 2 section 4): same owner,
 * no longer quarantined, a valid `adoptedAt` no earlier than the listing (less
 * one second), a safe-integer `adoptedAuthEpoch`, and a legal transition from
 * the listed row under the capture-row guard, which compares every frozen
 * field, the evidence, the identity and the first persistence exactly as
 * capture verification does (later processing is fine; a removed Blob only
 * through a proved upload). The guard, not the strict snapshot, decides the
 * identity here: a registry merge rewrites absent frozen fields as present and
 * `undefined` (for example `scriptureAnchor`), which the strict snapshot
 * would wrongly treat as a different capture. The caller checks the binding
 * and the current auth user. Never inferred from `adoptedAt` alone.
 */
export function keptCaptureAdoptedBySameCapture(
  current: AudioQueueRow | undefined,
  listed: AudioQueueRow,
  ownerId: string,
  listedAtMs: number,
): boolean {
  try {
    if (!current || !isPlainRecord(current) || !isPlainRecord(listed)) return false;
    if (current.ownerId !== ownerId || listed.ownerId !== ownerId) return false;
    if (malformedCaptureRecord(listed) !== null || malformedCaptureRecord(current) !== null) return false;
    if (current.localDisposition === 'quarantined' || current.quarantineReason !== undefined) return false;
    const adoptedAtMs = typeof current.adoptedAt === 'string' ? Date.parse(current.adoptedAt) : Number.NaN;
    if (!Number.isFinite(adoptedAtMs) || adoptedAtMs < listedAtMs - 1_000) return false;
    if (!isSafeEpoch(current.adoptedAuthEpoch)) return false;
    // Each IndexedDB read returns a new Blob object: the same evidence size
    // and type stand for the same stored Blob.
    const view: AudioQueueRow = { ...listed };
    const evidence = listed.captureEvidence as CaptureEvidenceV1 | undefined;
    if (
      view.blob instanceof Blob &&
      current.blob instanceof Blob &&
      evidence !== undefined &&
      current.blob.size === evidence.sizeBytes &&
      current.blob.size === view.blob.size &&
      current.blob.type === view.blob.type
    ) {
      view.blob = current.blob;
    }
    return captureRowTransitionRefusal(view, current) === null;
  } catch {
    return false;
  }
}

export interface KeptCaptureAdoptionInput {
  rowId: string;
  ownerId: string;
  /** The auth epoch captured at the tap; the write requires it still current. */
  authEpoch: number;
  /** `strictRowSnapshot` of the row as listed. */
  snapshot: string;
  /** Text evidence only: the exact text whose SHA-256 matched the evidence. */
  verifiedText: string | null;
  /** A testimony or update destination: the prayer must still exist. */
  destinationPrayerId: string | null;
  adoptedAt: string;
}

export type KeptCaptureAdoptionResult =
  | { status: 'adopted'; row: AudioQueueRow }
  | { status: 'refused'; reason: string };

/**
 * Adopt one kept recording (plan 9.2): one transaction over the queue, the
 * owner binding and the prayers (read only). Re-checks the binding, the exact
 * row snapshot, eligibility, the verified text, the destination prayer and,
 * synchronously right before the write, the auth user and epoch. Then sets
 * `localDisposition: 'unresolved'`, removes `quarantineReason` and records
 * `adoptedAt` / `adoptedAuthEpoch`; the capture-row guard must pass (frozen
 * fields and the payload untouched). A Blob recording stores a verified copy of
 * its own bytes (KR8, "Verified capture copies"): read and proved before the
 * transaction, applied only when every check above passed against the row read
 * inside it; bytes that cannot be read or verified refuse the adoption
 * (`bytes-...`) and leave the kept row exactly as it was. A refusal writes
 * nothing and is returned, not thrown; only an IndexedDB failure throws (and
 * writes nothing).
 */
export async function adoptKeptCapture(
  input: KeptCaptureAdoptionInput,
  readAuth: () => CaptureAuthSnapshot,
): Promise<KeptCaptureAdoptionResult> {
  assertDbWrite('db:audio-queue:adopt-kept-capture');
  // Hashing is not IndexedDB work: done before the transaction opens.
  let verifiedTextSha256: string | null = null;
  if (input.verifiedText !== null) {
    const encoded = new TextEncoder().encode(input.verifiedText);
    verifiedTextSha256 = await sha256Hex(
      encoded.buffer.slice(encoded.byteOffset, encoded.byteOffset + encoded.byteLength),
    );
  }
  // Reading and proving the bytes is not IndexedDB work either. Its result is
  // used only after every check below passes against the row read in the
  // transaction, so each existing refusal keeps its reason.
  const materialized = await materializeVerifiedCaptureCopy(input.rowId, input.ownerId);
  const db = await getDb();
  const tx = db.transaction([AUDIO_QUEUE_STORE, APP_META_STORE, PRAYERS_STORE], 'readwrite');
  void tx.done.catch(() => {});
  const refuse = async (reason: string): Promise<KeptCaptureAdoptionResult> => {
    try {
      tx.abort();
    } catch {
      // Already finished or aborted: nothing was written either way.
    }
    await tx.done.catch(() => {});
    return { status: 'refused', reason };
  };
  const binding = await tx.objectStore(APP_META_STORE).get(CONTENT_OWNER_META_KEY);
  if (binding?.value !== input.ownerId) return refuse('owner-binding');
  const queue = tx.objectStore(AUDIO_QUEUE_STORE);
  const stored = await queue.get(input.rowId);
  if (!stored) return refuse('row-missing');
  if (strictRowSnapshot(stored) !== input.snapshot) return refuse('row-changed');
  const reason = keptCaptureRefusal(stored, input.ownerId);
  if (reason) return refuse(reason);
  const evidence = stored.captureEvidence as CaptureEvidenceV1;
  if (
    evidence.kind === 'text' &&
    (stored.transcribedText !== input.verifiedText || verifiedTextSha256 !== evidence.sha256)
  ) {
    return refuse('text-unverified');
  }
  if (input.destinationPrayerId !== null) {
    if (stored.prayerId !== input.destinationPrayerId) return refuse('destination-mismatch');
    const prayer = await tx.objectStore(PRAYERS_STORE).get(input.destinationPrayerId);
    if (!prayer) return refuse('destination-unavailable');
  }
  let next: AudioQueueRow = {
    ...stored,
    localDisposition: 'unresolved',
    adoptedAt: input.adoptedAt,
    adoptedAuthEpoch: input.authEpoch,
  };
  delete next.quarantineReason;
  if (evidence.kind === 'blob') {
    // The verified copy, checked against the row read in this transaction:
    // never empty bytes, never the Blob this read returned.
    if (materialized.status !== 'verified') {
      return refuse(materialized.status === 'unverified' ? `bytes-${materialized.reason}` : 'bytes-unavailable');
    }
    const copied = keepVerifiedCaptureCopy(stored, next, materialized.copy);
    if (copied.blob !== materialized.copy.blob) return refuse('bytes-changed');
    next = copied;
  }
  // After every read and immediately before the write.
  const auth = readAuth();
  if (auth.userId !== input.ownerId || auth.epoch !== input.authEpoch) return refuse('auth-changed');
  const refusal = captureRowTransitionRefusal(stored, next);
  if (refusal) return refuse(`transition-${refusal.reason}`);
  await queue.put(next);
  await tx.done;
  return { status: 'adopted', row: next };
}

export async function saveAudioQueueRow(
  row: AudioQueueRow,
  expectedOwnerId?: string,
): Promise<void> {
  assertDbWrite('db:audio-queue:put');
  const db = await getDb();
  // Only commitCaptureRow writes capture fields.
  if (isCaptureRow(row)) throw new CaptureRowTransitionError('capture-fields-added');
  if (!expectedOwnerId) {
    if (row.audioProtocolVersion === 1 || row.ownerId !== undefined) {
      throw new Error('audio-queue-owner-required');
    }
    const legacyTx = db.transaction(AUDIO_QUEUE_STORE, 'readwrite');
    const existing = await legacyTx.store.get(row.id);
    if (existing && isCaptureRow(existing)) {
      return abortForCaptureRefusal(legacyTx, new Error('audio-queue-capture-row-exists'));
    }
    void legacyTx.store.put(row);
    await legacyTx.done;
    return;
  }
  const tx = db.transaction([AUDIO_QUEUE_STORE, APP_META_STORE], 'readwrite');
  const binding = await tx.objectStore(APP_META_STORE).get(CONTENT_OWNER_META_KEY);
  if (
    binding?.value !== expectedOwnerId ||
    (row.ownerId !== undefined && row.ownerId !== expectedOwnerId)
  ) {
    tx.abort();
    await tx.done.catch(() => {});
    throw new Error('audio-queue-owner-changed');
  }
  // A capture row is never replaced by a generic save (registry discovery's
  // check-then-save falls back to its merge path instead).
  const existing = await tx.objectStore(AUDIO_QUEUE_STORE).get(row.id);
  if (existing && isCaptureRow(existing)) {
    return abortForCaptureRefusal(tx, new Error('audio-queue-capture-row-exists'));
  }
  void tx.objectStore(AUDIO_QUEUE_STORE).put(row);
  await tx.done;
}

export async function getAudioQueueRow(id: string): Promise<AudioQueueRow | undefined> {
  const db = await getDb();
  return db.get(AUDIO_QUEUE_STORE, id);
}

/**
 * Owner-checked compare-at-write seam for work that crosses a network await.
 * KR8: a write here that carries the row's recording forward stores a verified
 * copy of it (`verifiedCaptureCopyBeforeWrite`): the caller's `verifiedCopy`,
 * or, when the caller passes none (`undefined`), one proved now, before the
 * transaction. `null` means no copy (a write that releases the Blob, or a
 * caller that already tried). The copy is used only when the guard accepts it
 * for the row read here; otherwise the write is exactly as without it.
 * Returns the row as written.
 */
export async function updateAudioQueueRowIfPresent(
  id: string,
  expectedOwnerId: string,
  update: (current: AudioQueueRow) => AudioQueueRow | null,
  verifiedCopy?: VerifiedCaptureCopy | null,
  options?: RecordingWriteOptions,
): Promise<AudioQueueRow | null> {
  assertDbWrite('db:audio-queue:update-if-present');
  const copy = await verifiedCaptureCopyBeforeWrite(id, expectedOwnerId, verifiedCopy, options);
  const db = await getDb();
  const tx = db.transaction([AUDIO_QUEUE_STORE, APP_META_STORE], 'readwrite');
  const binding = await tx.objectStore(APP_META_STORE).get(CONTENT_OWNER_META_KEY);
  const queue = tx.objectStore(AUDIO_QUEUE_STORE);
  const current = await queue.get(id);
  if (
    binding?.value !== expectedOwnerId ||
    !current ||
    (current.ownerId !== undefined && current.ownerId !== expectedOwnerId)
  ) {
    await tx.done;
    return null;
  }
  const proposed = update(current);
  if (!proposed) {
    await tx.done;
    return null;
  }
  const next = keepVerifiedCaptureCopy(current, proposed, copy);
  if (next.id !== id) {
    tx.abort();
    await tx.done.catch(() => {});
    throw new Error('audio-queue-id-change-refused');
  }
  if (next.ownerId !== current.ownerId) {
    tx.abort();
    await tx.done.catch(() => {});
    throw new Error('audio-queue-owner-change-refused');
  }
  const captureRefusal = captureRowTransitionRefusal(current, next);
  if (captureRefusal) return abortForCaptureRefusal(tx, captureRefusal);
  // A failed put (a storage failure; or, in WebKit, a Blob that cannot be read
  // when no copy read was made for it) aborts the transaction; that abort is
  // reported once, by `tx.done`, never also as an unhandled rejection of the
  // request (A2).
  observeQueuedWrite(queue.put(next));
  await tx.done;
  return next;
}

export interface RearmedPendingPrayerTranscription {
  prayer: Prayer;
  row: AudioQueueRow;
}

/** Atomically re-arm both halves of a failed new-prayer transcription. */
export async function rearmPendingPrayerTranscription(
  prayerId: string,
  queueItemId: string,
  expectedOwnerId: string,
  verifiedCopy?: VerifiedCaptureCopy | null,
): Promise<RearmedPendingPrayerTranscription | null> {
  assertDbWrite('db:prayer-audio:rearm');
  // KR8: re-arming keeps the recording; it stores a verified copy.
  const copy = await verifiedCaptureCopyBeforeWrite(queueItemId, expectedOwnerId, verifiedCopy);
  const db = await getDb();
  const tx = db.transaction(
    [
      AUDIO_QUEUE_STORE,
      PRAYERS_STORE,
      APP_META_STORE,
      KEEPSAKE_FENCES_STORE,
      KEEPSAKE_META_STORE,
    ],
    'readwrite',
  );
  const binding = await tx.objectStore(APP_META_STORE).get(CONTENT_OWNER_META_KEY);
  const queue = tx.objectStore(AUDIO_QUEUE_STORE);
  const prayers = tx.objectStore(PRAYERS_STORE);
  const row = await queue.get(queueItemId);
  const prayer = await prayers.get(prayerId);
  if (
    binding?.value !== expectedOwnerId ||
    !row ||
    row.prayerId !== prayerId ||
    row.failed !== true ||
    !prayer ||
    prayer.pendingTranscriptionId !== queueItemId
  ) {
    await tx.done;
    return null;
  }
  const nextRow = keepVerifiedCaptureCopy(row, { ...row, attemptCount: 0, failed: false }, copy);
  const nextPrayer: Prayer = {
    ...prayer,
    transcriptionFailed: undefined,
  };
  const captureRefusal = captureRowTransitionRefusal(row, nextRow);
  if (captureRefusal) return abortForCaptureRefusal(tx, captureRefusal);
  // As in failPendingPrayerTranscription: an abort is reported once.
  void tx.done.catch(() => {});
  observeQueuedWrite(queue.put(nextRow));
  observeQueuedWrite(prayers.put(nextPrayer));
  const marks = renderRelevantChanged(prayer, nextPrayer)
    ? await bumpFenceAndEpochInTx(tx as unknown as KeepsakeWriteTx, prayer.id, 'local')
    : null;
  await tx.done;
  if (marks) afterFenceWrite(prayer.id, marks.seq, marks.epoch);
  return { prayer: nextPrayer, row: nextRow };
}

export async function getAllAudioQueueRows(): Promise<AudioQueueRow[]> {
  const db = await getDb();
  const all = await db.getAll(AUDIO_QUEUE_STORE);
  // Oldest first — the processor drains in capture order so the user's
  // earliest audio gets transcribed before any later interruptions.
  return all.sort(
    (a, b) => new Date(a.capturedAt).getTime() - new Date(b.capturedAt).getTime()
  );
}

/**
 * Exact local snapshot authorized by a just-verified terminal receipt or
 * explicit browser-only discard. The receipt proves one registry/content
 * tuple; it must never become a reusable permission to delete a row that was
 * rebound while the network proof was in flight.
 */
export type AudioQueueDeletionAuthority = {
  kind:
    | 'registry-deletion-receipt'
    | 'content-receipt'
    | 'explicit-browser-discard';
  receiptId: string | null;
  expectedRegistryRevision: string | null;
  expectedDestinationOpId: string | null;
  expectedDestinationEntityType: 'prayer' | 'bookmark' | null;
  expectedDestinationEntityId: string | null;
  expectedDestinationSubId: string | null;
};

export async function deleteAudioQueueRow(
  id: string,
  expectedOwnerId: string,
  authority?: AudioQueueDeletionAuthority,
): Promise<boolean> {
  assertDbWrite('db:audio-queue:delete');
  const db = await getDb();
  const tx = db.transaction([AUDIO_QUEUE_STORE, APP_META_STORE], 'readwrite');
  const queue = tx.objectStore(AUDIO_QUEUE_STORE);
  const [binding, row] = await Promise.all([
    tx.objectStore(APP_META_STORE).get(CONTENT_OWNER_META_KEY),
    queue.get(id),
  ]);
  if (
    binding?.value !== expectedOwnerId ||
    row?.ownerId !== expectedOwnerId
  ) {
    tx.abort();
    await tx.done.catch(() => {});
    throw new Error('audio-queue-owner-changed');
  }
  // Physical removal is never authorized by feature state or matching text.
  // The higher layer must present the exact receipt-bound snapshot obtained
  // immediately beforehand, and this transaction repeats every tuple field so
  // a cross-tab rebind cannot launder a stale receipt into deletion authority.
  const exactSnapshot = Boolean(
    authority &&
    row &&
    (row.registryRevision ?? null) === authority.expectedRegistryRevision &&
    (row.destinationOpId ?? null) === authority.expectedDestinationOpId &&
    (row.destinationEntityType ?? null) === authority.expectedDestinationEntityType &&
    (row.destinationEntityId ?? null) === authority.expectedDestinationEntityId &&
    (row.destinationSubId ?? null) === authority.expectedDestinationSubId
  );
  if (
    !row ||
    row.audioProtocolVersion !== 1 ||
    !row.destinationId ||
    row.localDisposition === 'quarantined' ||
    row.recoveryKind === 'unknown' ||
    !exactSnapshot ||
    (row.captureMode === 'enhanced' && (
      authority?.kind !== 'registry-deletion-receipt' ||
      !authority.receiptId
    )) ||
    (row.captureMode === 'browser' &&
      authority?.kind !== 'content-receipt' &&
      authority?.kind !== 'explicit-browser-discard') ||
    (authority?.kind === 'content-receipt' && (
      !authority.receiptId ||
      authority.receiptId !== authority.expectedDestinationOpId
    )) ||
    (row.captureMode !== 'enhanced' && row.captureMode !== 'browser')
  ) {
    await tx.done;
    return false;
  }
  void queue.delete(id);
  await tx.done;
  return true;
}

// ---- Bible bookmarks (verse-selection bookmarks + notes) ----
//
// Thin IDB wrappers. Tombstone semantics (soft-delete via saveBookmark with
// deletedAt; purge confirmed-synced tombstones) live in bookmark-store.ts.
// `getAllBookmarks` returns EVERY row including tombstones — callers filter
// `deletedAt` for display. `hardDeleteBookmark` is the physical delete used
// only by the confirmed-tombstone purge, never by the user-facing delete.

export async function getAllBookmarks(): Promise<BibleBookmark[]> {
  const db = await getDb();
  return db.getAll(BIBLE_BOOKMARKS_STORE);
}

export async function getBookmarksForChapter(
  bookSlug: string,
  chapter: number,
): Promise<BibleBookmark[]> {
  const db = await getDb();
  return db.getAllFromIndex(
    BIBLE_BOOKMARKS_STORE,
    'by-book-chapter',
    IDBKeyRange.only([bookSlug, chapter]),
  );
}

export async function getBookmark(
  id: string,
): Promise<BibleBookmark | undefined> {
  const db = await getDb();
  return db.get(BIBLE_BOOKMARKS_STORE, id);
}

export async function saveBookmark(bookmark: BibleBookmark): Promise<void> {
  assertDbWrite('db:bookmark:put');
  const db = await getDb();
  await db.put(BIBLE_BOOKMARKS_STORE, bookmark);
}

export async function hardDeleteBookmark(id: string): Promise<void> {
  assertDbWrite('db:bookmark:delete');
  const db = await getDb();
  await db.delete(BIBLE_BOOKMARKS_STORE, id);
}

// Bible highlights (Chunk C). Same thin-wrapper + tombstone semantics as
// bookmarks (soft-delete via saveHighlight with deletedAt; purge
// confirmed-synced tombstones in highlight-store.loadHighlights).
// `getAllHighlights` returns EVERY row including tombstones — callers filter
// `deletedAt` for display. `hardDeleteHighlight` is the physical delete used
// only by the confirmed-tombstone purge + the remote-deleted pull branch.

export async function getAllHighlights(): Promise<BibleHighlight[]> {
  const db = await getDb();
  return db.getAll(BIBLE_HIGHLIGHTS_STORE);
}

export async function getHighlightsForChapter(
  bookSlug: string,
  chapter: number,
): Promise<BibleHighlight[]> {
  const db = await getDb();
  return db.getAllFromIndex(
    BIBLE_HIGHLIGHTS_STORE,
    'by-book-chapter',
    IDBKeyRange.only([bookSlug, chapter]),
  );
}

export async function getHighlight(
  id: string,
): Promise<BibleHighlight | undefined> {
  const db = await getDb();
  return db.get(BIBLE_HIGHLIGHTS_STORE, id);
}

export async function saveHighlight(highlight: BibleHighlight): Promise<void> {
  assertDbWrite('db:highlight:put');
  const db = await getDb();
  await db.put(BIBLE_HIGHLIGHTS_STORE, highlight);
}

export async function hardDeleteHighlight(id: string): Promise<void> {
  assertDbWrite('db:highlight:delete');
  const db = await getDb();
  await db.delete(BIBLE_HIGHLIGHTS_STORE, id);
}
